// Rarity effects: the burst of light when a card lands face-up on the table.
// Colors live in src/config.ts (playEffects.colors). The recipes below decide
// what each rarity's effect is made of; each line is one layer of the effect.
import * as THREE from 'three';
import { CONFIG } from '../../config.ts';
import { cardPlane } from './cardShape.ts';

type Kind = 'banner' | 'doors' | 'twister' | 'ripple' | 'glow' | 'orbit' | 'burst' | 'shockwave' | 'flash' | 'shake' | 'rise' | 'pillar' | 'streak' | 'cracks' | 'shards' | 'gather' | 'beam' | 'puff' | 'drift' | 'fall' | 'twinkle' | 'dust' | 'build'
  | 'seep' | 'wisp' | 'sheen' | 'rumble' | 'glide' | 'riser' | 'bolt' | 'flock' | 'spiral' | 'souls' | 'flames' | 'slash' | 'fireflies' | 'lift' | 'strike' | 'vortex' | 'geyser' | 'halo' | 'rimpuff' | 'rocks' | 'pit' | 'vines' | 'planets';

const BLACK = new THREE.Color(0x000000);

/** How much of a 'beam' layer's time is spent fading in, and again fading out (a looping effect overlaps its rounds by this much). */
export const BEAM_FADE = 0.15;

/** Seconds into the sacrifice effect at which the card bursts apart (the table hides the card at this moment). */
export const SACRIFICE_BURST = 0.85;

interface Layer {
  kind: Kind;
  color?: number; // which color from the rarity's list (0 = main, 1 = accent)
  delay?: number; // seconds before this layer starts
  duration: number; // seconds
  size?: number; // how far it spreads
  strength?: number; // brightness
  count?: number; // number of sparks / trails
  pulses?: number; // how many times an aura breathes
  speed?: number; // laps per second for orbiting trails
  dark?: boolean; // draw as a dark smoke instead of light (gather, wisp: dark sparks or curls, painted over the table)
  dot?: number; // how big each particle is, in cm (puff, drift, fall, twinkle)
  flip?: boolean; // streak: the slashes run along the other diagonal (two streak layers, one flipped, make an X)
  toBlack?: boolean; // puff: each cloud starts in its color and darkens to black as it rises
  hold?: boolean; // cracks: already spread from the start, fading in and out (cracks that stay)
  scale?: number; // cracks: how big, against the card (1 = the card; Earthquake's are smaller than Tectonic Shift's)
  line?: number; // cracks: which color the crack lines themselves are drawn in (left out: near-black, with `color` as their glowing edge)
}

// ---------------- the recipes ----------------
const RECIPES: Record<string, Layer[]> = {
  // a small, quick grey ripple
  common: [{ kind: 'ripple', duration: 0.45, size: 1.4, strength: 0.7 }],
  // a slightly larger light green ripple
  uncommon: [{ kind: 'ripple', duration: 0.6, size: 1.8, strength: 0.8 }],
  // a noticeable light blue double ripple
  rare: [
    { kind: 'glow', duration: 0.8, strength: 0.55 },
    { kind: 'ripple', duration: 0.7, size: 2.1, strength: 0.95 },
    { kind: 'ripple', delay: 0.15, duration: 0.7, size: 1.7, strength: 0.6 },
  ],
  // a rich deep blue glow with two ripples
  'ultra-rare': [
    { kind: 'glow', duration: 1.1, strength: 1 },
    { kind: 'ripple', color: 1, duration: 0.8, size: 2.4, strength: 1 },
    { kind: 'ripple', delay: 0.18, duration: 0.9, size: 2.0, strength: 0.9 },
  ],
  // a maroon aura that breathes twice
  epic: [
    { kind: 'glow', duration: 1.9, strength: 1.4, pulses: 2, size: 1.15 },
    { kind: 'glow', color: 1, duration: 1.1, strength: 0.6 },
    { kind: 'ripple', color: 1, duration: 0.9, size: 2.6, strength: 0.9 },
  ],
  // a purple aura with lavender trails circling and sparks
  mythic: [
    { kind: 'glow', duration: 2.1, strength: 1.4, pulses: 2, size: 1.2 },
    { kind: 'orbit', color: 1, duration: 1.5, count: 2, speed: 1.4 },
    { kind: 'ripple', color: 1, duration: 1.0, size: 2.8 },
    { kind: 'burst', color: 1, delay: 0.1, count: 14, duration: 1.1, size: 7 },
  ],
  // a golden pulse with a ring of light racing around the card
  legendary: [
    { kind: 'flash', color: 1, duration: 0.35 },
    { kind: 'glow', duration: 1.9, strength: 1.5, pulses: 1, size: 1.2 },
    { kind: 'orbit', duration: 0.95, count: 2, speed: 2.4 },
    { kind: 'ripple', duration: 0.9, size: 3.0 },
    { kind: 'ripple', color: 1, delay: 0.2, duration: 0.8, size: 2.2 },
  ],
  // a regal white flash, gold aura, white and gold rings, slow gold trails and sparkles
  grand: [
    { kind: 'flash', duration: 0.45 },
    { kind: 'glow', color: 1, duration: 2.5, strength: 1.6, pulses: 2, size: 1.25 },
    { kind: 'glow', duration: 1.4, strength: 0.8 },
    { kind: 'ripple', duration: 1.0, size: 3.2 },
    { kind: 'ripple', color: 1, delay: 0.22, duration: 1.0, size: 2.6 },
    { kind: 'orbit', color: 1, duration: 1.9, count: 3, speed: 1.1 },
    { kind: 'burst', color: 1, delay: 0.1, count: 22, duration: 1.4, size: 9 },
    { kind: 'burst', delay: 0.2, count: 12, duration: 1.2, size: 6 },
  ],
  // a menacing impact: the table shakes, shockwaves, a dark maroon smoke with
  // a sickly green core, and embers thrown outward
  ancient: [
    { kind: 'shake', duration: 0.5, strength: 0.7 },
    { kind: 'flash', color: 0, duration: 0.25, strength: 1.6 },
    { kind: 'shockwave', duration: 0.6, size: 34, strength: 1.4 },
    { kind: 'shockwave', color: 1, delay: 0.12, duration: 0.75, size: 24, strength: 1.2 },
    { kind: 'glow', duration: 2.8, strength: 1.4, pulses: 3, size: 1.6, dark: true },
    { kind: 'glow', duration: 2.6, strength: 2, pulses: 3, size: 1.3 },
    { kind: 'glow', color: 1, delay: 0.25, duration: 2.3, strength: 1.6, pulses: 2, size: 1.05 },
    { kind: 'burst', count: 28, duration: 1.4, size: 13, strength: 1.4 },
    { kind: 'burst', color: 1, delay: 0.1, count: 18, duration: 1.2, size: 9, strength: 1.2 },
  ],
  // royal and quick: a light blue flash, gold and blue rings, a gold trail and sparkles
  // ---- not rarities: tier-ups and Ambush ----
  // "powered up": a column of light and sparks rising off the card
  tier2: [
    { kind: 'flash', color: 1, duration: 0.3 },
    { kind: 'pillar', duration: 1.1, size: 9, strength: 0.75 },
    { kind: 'glow', duration: 1.6, strength: 1.3, pulses: 2, size: 1.15 },
    { kind: 'ripple', duration: 0.8, size: 2.4 },
    { kind: 'rise', color: 1, count: 16, duration: 1.3, size: 8 },
    { kind: 'rise', delay: 0.15, count: 12, duration: 1.2, size: 6 },
  ],
  // the same idea turned up: taller, hotter, longer, with a shockwave, circling trails and a small shake
  tier3: [
    { kind: 'shake', duration: 0.35, strength: 0.35 },
    { kind: 'flash', color: 1, duration: 0.4, strength: 1.5 },
    { kind: 'shockwave', duration: 0.6, size: 26, strength: 1.2 },
    { kind: 'pillar', duration: 1.9, size: 16, strength: 1 },
    { kind: 'pillar', color: 1, delay: 0.1, duration: 1.5, size: 11, strength: 0.7 },
    { kind: 'glow', duration: 2.6, strength: 1.8, pulses: 3, size: 1.35 },
    { kind: 'ripple', duration: 0.9, size: 3.2 },
    { kind: 'ripple', color: 1, delay: 0.2, duration: 0.9, size: 2.5 },
    { kind: 'orbit', color: 1, duration: 2.0, count: 3, speed: 1.8 },
    { kind: 'rise', color: 1, count: 30, duration: 1.8, size: 13 },
    { kind: 'rise', delay: 0.2, count: 26, duration: 1.7, size: 10 },
    { kind: 'rise', color: 1, delay: 0.6, count: 20, duration: 1.5, size: 9 },
    { kind: 'burst', delay: 0.05, count: 20, duration: 1.1, size: 10 },
  ],
  // ability "entrance": the card has just slammed onto the table. A jolt, a white flash under it, a shockwave
  // and a tight ring racing outward, dust rolling away along the table, and sparks thrown up from its edges.
  // (The fall itself is the card's own movement: abilityEffects.entrance in src/config.ts.)
  entrance: [
    { kind: 'shake', duration: 0.26, strength: 0.28 },
    { kind: 'flash', color: 1, duration: 0.32, strength: 1 },
    { kind: 'shockwave', duration: 0.5, size: 21, strength: 1.05 },
    { kind: 'ripple', color: 1, duration: 0.42, size: 2.7, strength: 1 },
    { kind: 'ripple', delay: 0.08, duration: 0.55, size: 2.1, strength: 0.7 },
    { kind: 'dust', color: 2, duration: 1.1, count: 14, size: 8.5, dot: 3.6, strength: 0.8 },
    { kind: 'burst', color: 1, count: 14, duration: 0.65, size: 9 },
    { kind: 'glow', delay: 0.05, duration: 0.8, strength: 0.9, size: 1.05 },
  ],
  // a card played for free: a small light blue beacon stands on it for a moment, with a ring at its foot
  // and a few sparks lifting off
  freePlay: [
    { kind: 'ripple', duration: 0.5, size: 1.5, strength: 0.8 },
    { kind: 'pillar', duration: 1.3, size: 8, strength: 0.7 },
    { kind: 'rise', color: 1, delay: 0.05, count: 9, duration: 1.2, size: 7 },
    { kind: 'glow', duration: 0.9, strength: 0.6, size: 0.9 },
  ],
  // a Building is played: it is built in front of you. Dust rolls out at its foot, a frame of light goes up
  // floor by floor on four corner posts with sparks flying as each floor is fixed, the finished frame flashes,
  // then sinks into the card and is gone.
  building: [
    { kind: 'dust', color: 2, duration: 0.9, count: 8, size: 5, dot: 2.6, strength: 0.45 },
    { kind: 'build', duration: 1.7, count: 4, size: 4.2, strength: 0.95 },
    { kind: 'burst', color: 1, delay: 0.3, count: 6, duration: 0.45, size: 4.5, strength: 0.9 },
    { kind: 'burst', color: 1, delay: 0.62, count: 6, duration: 0.45, size: 4.5, strength: 0.9 },
    { kind: 'burst', color: 1, delay: 0.94, count: 7, duration: 0.5, size: 5, strength: 0.9 },
    { kind: 'ripple', color: 1, delay: 1.3, duration: 0.5, size: 1.7, strength: 0.8 },
    { kind: 'glow', delay: 1.25, duration: 0.6, strength: 0.7, size: 0.95 },
  ],
  // a Building decays as its owner's turn ends: blue cracks run across it and hold for a moment, with a
  // thin blue ring and a few chips of light falling away
  spDecay: [
    { kind: 'cracks', color: 1, line: 0, duration: 1.6, strength: 1 },
    { kind: 'glow', duration: 1.0, strength: 0.4, size: 0.9 },
    { kind: 'ripple', color: 1, delay: 0.1, duration: 0.6, size: 1.7, strength: 0.6 },
    { kind: 'burst', color: 1, delay: 0.15, count: 7, duration: 0.7, size: 4.5, strength: 0.8 },
  ],
  // an Emperor uses one of its abilities: gold light swells under it, two rings go out, a short column
  // stands on it and sparks lift off its edges
  emperorAbility: [
    { kind: 'flash', color: 1, duration: 0.35, strength: 0.7 },
    { kind: 'glow', duration: 1.6, strength: 1.5, pulses: 2, size: 1.25 },
    { kind: 'ripple', duration: 0.7, size: 2.8, strength: 1 },
    { kind: 'ripple', color: 1, delay: 0.18, duration: 0.8, size: 2.2, strength: 0.8 },
    { kind: 'pillar', duration: 1.2, size: 10, strength: 0.7 },
    { kind: 'rise', color: 1, delay: 0.05, count: 18, duration: 1.4, size: 9 },
  ],
  // ability "passive": one soft breath of light and a thin ring
  passive: [
    { kind: 'glow', duration: 1.4, strength: 0.55, size: 0.9 },
    { kind: 'ripple', delay: 0.1, duration: 0.9, size: 1.5, strength: 0.45 },
  ],
  // ---------------- the secret rares ----------------
  // The Remaining moves within a pile: deep red spirits circle the pile and rise off it, over a dark swell
  remainingJump: [
    { kind: 'wisp', color: 2, dark: true, duration: 2.2, count: 5, size: 8, strength: 1, dot: 1.2 },
    { kind: 'glow', duration: 2.2, strength: 0.75, pulses: 2, size: 1.3 },
    { kind: 'orbit', duration: 2.0, count: 4, speed: 1.3 },
    { kind: 'orbit', color: 1, delay: 0.25, duration: 1.8, count: 3, speed: 1.9 },
    { kind: 'rise', color: 1, delay: 0.5, count: 16, duration: 1.7, size: 11, strength: 0.9 },
    { kind: 'riser', delay: 0.9, count: 5, duration: 1.3, size: 12, strength: 0.9 },
  ],
  // a Calling Pillar's beacon: played again and again so it stands steady. One Pillar: faint and slim; each
  // more Pillar its owner has in play, taller and brighter (aura-pillar-1 … aura-pillar-5)
  'aura-pillar-1': [
    { kind: 'beam', duration: 4, size: 8, strength: 0.7 },
    { kind: 'glow', duration: 4, strength: 0.45, size: 0.85 },
  ],
  'aura-pillar-2': [
    { kind: 'beam', duration: 4, size: 11, strength: 0.9 },
    { kind: 'glow', duration: 4, strength: 0.6, size: 0.95 },
    { kind: 'rise', color: 1, count: 3, duration: 4, size: 7, strength: 0.6 },
  ],
  'aura-pillar-3': [
    { kind: 'beam', duration: 4, size: 15, strength: 1.1 },
    { kind: 'glow', duration: 4, strength: 0.75, size: 1.05 },
    { kind: 'rise', color: 1, count: 6, duration: 4, size: 10, strength: 0.75 },
  ],
  'aura-pillar-4': [
    { kind: 'beam', duration: 4, size: 20, strength: 1.3 },
    { kind: 'glow', duration: 4, strength: 0.95, size: 1.2 },
    { kind: 'rise', color: 1, count: 10, duration: 4, size: 14, strength: 0.9 },
    { kind: 'orbit', color: 1, duration: 4, count: 2, speed: 0.6 },
  ],
  'aura-pillar-5': [
    { kind: 'beam', duration: 4, size: 27, strength: 1.6 },
    { kind: 'glow', duration: 4, strength: 1.15, size: 1.4 },
    { kind: 'rise', color: 1, count: 16, duration: 4, size: 20, strength: 1 },
    { kind: 'orbit', color: 1, duration: 4, count: 4, speed: 0.9 },
  ],
  // Realm Beyond Comprehension: red swirls turning over the card, and red sparks thrown off above it at random
  'aura-realm': [
    { kind: 'glow', color: 2, duration: 3.6, strength: 0.4, pulses: 1, size: 1.1 },
    { kind: 'orbit', duration: 3.6, count: 3, speed: 0.7 },
    { kind: 'orbit', color: 1, delay: 0.4, duration: 3.0, count: 2, speed: 1.2 },
    { kind: 'wisp', duration: 3.6, count: 4, size: 6, strength: 0.7, dot: 1 },
    { kind: 'twinkle', color: 1, count: 7, duration: 3.6, strength: 1, dot: 0.8 },
    { kind: 'burst', color: 1, delay: 1.3, count: 6, duration: 0.7, size: 5 },
    { kind: 'burst', color: 1, delay: 2.6, count: 5, duration: 0.7, size: 4 },
  ],
  // The Remaining, in play: a deep red glow round its card fades in and out, and red and black sparks are pulled
  // in toward it from all around. Played again and again, each round starting as the last one fades.
  'aura-remaining': [
    { kind: 'glow', duration: 4, strength: 1.1, pulses: 1, size: 1.55 },
    { kind: 'glow', color: 1, delay: 0.6, duration: 2.8, strength: 0.45, pulses: 1, size: 1.15 },
    { kind: 'gather', delay: 0.3, duration: 1.6, count: 14, size: 12, strength: 1 },
    { kind: 'gather', color: 1, delay: 0.45, duration: 1.5, count: 8, size: 10, strength: 0.8 },
    { kind: 'gather', color: 2, dark: true, delay: 0.4, duration: 1.55, count: 14, size: 12, strength: 1 },
    { kind: 'gather', delay: 2.2, duration: 1.5, count: 10, size: 11, strength: 0.9 },
    { kind: 'gather', color: 2, dark: true, delay: 2.3, duration: 1.5, count: 10, size: 11, strength: 1 },
  ],
  // Recall Rift: pink and blue ribbons wind up round the card it returns to its owner's hand, a glow swells under
  // them, and the card is gone in a soft flash at the top (RECALL_SECONDS in TableView: the card stays that long)
  'recall-swirl': [
    { kind: 'glow', duration: 1.6, strength: 0.8, pulses: 1, size: 1.2 },
    { kind: 'glow', color: 1, delay: 0.2, duration: 1.3, strength: 0.6, pulses: 1, size: 1.0 },
    { kind: 'spiral', duration: 1.6, count: 4, size: 11, speed: 1.6, strength: 1.3, dot: 1.6 },
    { kind: 'spiral', color: 1, delay: 0.15, duration: 1.5, count: 4, size: 10, speed: -1.4, strength: 1.2, dot: 1.4 },
    { kind: 'twinkle', color: 2, delay: 0.3, count: 8, duration: 1.3, strength: 1, dot: 0.7 },
    { kind: 'flash', color: 2, delay: 1.2, duration: 0.4, strength: 0.9 },
  ],
  // J'miele's Hidden Veil: a pale mist drifts round the Unit and a few frost-white glints wink through it
  'aura-veiled': [
    { kind: 'glow', duration: 3.2, strength: 0.75, pulses: 1, size: 1.25 },
    { kind: 'halo', color: 2, delay: 0.2, duration: 2.8, size: 1.25, strength: 0.6, pulses: 1 },
    { kind: 'puff', count: 4, duration: 3.2, size: 5, strength: 0.75, dot: 2.8 },
    { kind: 'twinkle', color: 1, delay: 0.4, count: 7, duration: 2.8, strength: 1.1, dot: 0.7 },
  ],
  'veil-cast': [
    { kind: 'puff', count: 6, duration: 1.4, size: 6, strength: 0.8, dot: 3 },
    { kind: 'sheen', color: 1, delay: 0.2, duration: 0.8, strength: 0.7 },
    { kind: 'twinkle', color: 1, delay: 0.3, count: 8, duration: 1.2, strength: 1, dot: 0.8 },
  ],
  // Scackle's Emergency Exit: a teal bubble of water wobbling round the Emperor while nothing but Pierce gets through
  'aura-exit': [
    { kind: 'halo', duration: 2.6, size: 1.35, strength: 0.6, pulses: 2 },
    { kind: 'glow', color: 2, duration: 2.6, strength: 0.4, pulses: 1, size: 1.15 },
    { kind: 'rise', color: 1, delay: 0.3, count: 5, duration: 2.4, size: 4, strength: 0.7 },
  ],
  'exit-cast': [
    { kind: 'ripple', duration: 0.9, size: 2.4, strength: 0.9 },
    { kind: 'ripple', color: 1, delay: 0.25, duration: 0.9, size: 2.0, strength: 0.7 },
    { kind: 'halo', delay: 0.2, duration: 1.2, size: 1.4, strength: 1, pulses: 1 },
    { kind: 'rise', color: 1, delay: 0.2, count: 10, duration: 1.4, size: 6, strength: 1 },
  ],
  // Bastian's Traveler's Map: a parchment-gold wash over the Biome it switched off, with a compass ring turning slowly
  'aura-mapped': [
    { kind: 'glow', duration: 3.4, strength: 0.45, pulses: 1, size: 1.05 },
    { kind: 'orbit', color: 1, duration: 3.4, count: 4, speed: 0.3, strength: 0.7 },
    { kind: 'halo', color: 2, delay: 0.4, duration: 2.8, size: 1.2, strength: 0.4, pulses: 1 },
  ],
  'map-cast': [
    { kind: 'sheen', duration: 0.9, strength: 0.8 },
    { kind: 'halo', color: 1, duration: 1.2, size: 1.5, strength: 0.9, pulses: 1 },
    { kind: 'twinkle', color: 1, delay: 0.2, count: 7, duration: 1.2, strength: 1, dot: 0.7 },
  ],
  // Flamburst's Molten Coat: molten orange light round the Unit, embers rising off it, while its Shield holds
  'aura-coat': [
    { kind: 'glow', duration: 2.8, strength: 1.05, pulses: 1, size: 1.3 },
    { kind: 'flames', color: 0, delay: 0.2, duration: 2.4, count: 8, size: 1.8, strength: 0.8 },
    { kind: 'rise', color: 1, count: 8, duration: 2.6, size: 6, strength: 1 },
  ],
  'coat-cast': [
    { kind: 'flash', duration: 0.3, strength: 0.7 },
    { kind: 'flames', duration: 1.4, count: 12, size: 2.4, strength: 1 },
    { kind: 'rise', color: 1, delay: 0.1, count: 12, duration: 1.4, size: 7, strength: 1 },
    { kind: 'glow', delay: 0.1, duration: 1.2, strength: 1, pulses: 1, size: 1.3 },
  ],
  // Barragand's Burning Necrosis: blood seeps, sickly green rot glows and a little black smoke curls off the Unit
  'aura-necrosis': [
    { kind: 'glow', color: 1, duration: 2.8, strength: 0.6, pulses: 1, size: 1.15 },
    { kind: 'seep', duration: 3, count: 4, size: 4, dot: 2, strength: 1 },
    { kind: 'wisp', color: 2, dark: true, delay: 0.5, duration: 2.8, count: 4, size: 6, strength: 0.9, dot: 1.1 },
  ],
  'necrosis-cast': [
    { kind: 'puff', color: 2, dark: true, count: 5, duration: 1.3, size: 5, strength: 0.9, dot: 2.6 },
    { kind: 'seep', duration: 1.4, count: 5, size: 4.2, dot: 2.1, strength: 1 },
    { kind: 'burst', color: 1, delay: 0.2, count: 8, duration: 0.7, size: 5 },
  ],
  // Barragand's Heat Exhaustion: a burst of heat over the opposing Emperor, and a flare over the ability timer it lengthened
  'heat-flare': [
    { kind: 'flash', duration: 0.35, strength: 0.8 },
    { kind: 'shockwave', color: 2, duration: 0.7, size: 12, strength: 0.7 },
    { kind: 'rise', color: 1, delay: 0.1, count: 10, duration: 1.3, size: 7, strength: 1 },
  ],
  'heat-timer': [
    { kind: 'flash', duration: 0.4, strength: 0.9 },
    { kind: 'burst', color: 1, delay: 0.1, count: 10, duration: 0.7, size: 4 },
    { kind: 'ripple', color: 2, delay: 0.1, duration: 0.8, size: 1.8, strength: 0.8 },
  ],
  // Ailleiah gives Impervious Shield: a gold crest flares up round the Unit, gold motes rise and a royal ring rolls out
  'royal-ward-cast': [
    { kind: 'flash', color: 1, duration: 0.35, strength: 0.8 },
    { kind: 'glow', duration: 1.4, strength: 1.1, pulses: 1, size: 1.35 },
    { kind: 'shockwave', color: 2, delay: 0.05, duration: 0.7, size: 12, strength: 0.7 },
    { kind: 'rise', color: 1, delay: 0.1, count: 10, duration: 1.4, size: 7, strength: 1 },
    { kind: 'twinkle', color: 1, delay: 0.2, count: 9, duration: 1.3, strength: 1.1, dot: 0.9 },
  ],
  // Taunt: a red challenge blares out from the chosen Unit
  'taunt-cast': [
    { kind: 'flash', duration: 0.3, strength: 0.7 },
    { kind: 'ripple', duration: 0.8, size: 2.8, strength: 1 },
    { kind: 'ripple', color: 1, delay: 0.2, duration: 0.8, size: 2.4, strength: 0.8 },
    { kind: 'ripple', color: 2, delay: 0.4, duration: 0.8, size: 2.0, strength: 0.6 },
  ],
  // Arcane Ascension: dark magic bursts up round the Unit and violet light spirals into it
  'ascend-cast': [
    { kind: 'puff', color: 0, dark: true, count: 5, duration: 1.4, size: 5, strength: 0.9, dot: 2.6 },
    { kind: 'spiral', color: 1, duration: 1.4, count: 5, size: 11, speed: -1.6, strength: 1.4, dot: 1.6 },
    { kind: 'glow', color: 1, delay: 0.2, duration: 1.2, strength: 1, pulses: 1, size: 1.3 },
    { kind: 'flash', color: 2, delay: 1.0, duration: 0.35, strength: 0.8 },
  ],
  // Drifts Tarragon arrives: its wings beat, sand swirls up round it and a gust blows off toward the lane in front
  'sand-gust': [
    { kind: 'shockwave', color: 2, delay: 0.5, duration: 0.7, size: 16, strength: 0.9 },
    { kind: 'dust', delay: 0.5, count: 18, duration: 2.0, size: 14, strength: 1.2, dot: 6 },
    { kind: 'spiral', color: 1, delay: 0.5, duration: 1.6, count: 5, size: 10, speed: 1.4, strength: 1.3, dot: 1.8 },
    { kind: 'drift', color: 1, delay: 0.6, count: 9, duration: 1.6, size: 12, strength: 1.3, dot: 4 },
  ],
  // …a cloud of stone-powder sand engulfs each Unit in that lane, blinding it
  'sand-blind': [
    { kind: 'puff', delay: 0.7, count: 8, duration: 2.4, size: 7, strength: 1.25, dot: 5 },
    { kind: 'dust', color: 2, delay: 0.75, count: 14, duration: 2.0, size: 8, strength: 1.2, dot: 4.5 },
    { kind: 'drift', color: 1, delay: 0.85, count: 8, duration: 1.8, size: 10, strength: 1.3, dot: 3.5 },
    { kind: 'twinkle', color: 1, delay: 0.9, count: 8, duration: 1.6, strength: 1.1, dot: 0.8 },
  ],
  // …and while it may still miss, sand keeps blowing across it in a dull haze
  'aura-sandblind': [
    { kind: 'glow', color: 2, duration: 2.4, strength: 0.6, pulses: 1, size: 1.1 },
    { kind: 'drift', duration: 2.4, count: 6, size: 10, strength: 1.1, dot: 3.4 },
    { kind: 'puff', color: 1, delay: 0.6, count: 4, duration: 2.0, size: 5, strength: 0.7, dot: 3.6 },
  ],
  // Jouster picks its foe: a steel-white flash and red rings snap tight round it
  'joust-mark': [
    { kind: 'flash', duration: 0.3, strength: 0.8 },
    { kind: 'halo', color: 1, duration: 0.9, size: 1.6, strength: 1, pulses: 2 },
    { kind: 'burst', color: 1, delay: 0.1, count: 8, duration: 0.6, size: 5 },
  ],
  // Ailleiah's Impervious Shield on a card: a soft gold glow breathing round it, a slow ring of gold motes and a
  // regal shimmer across it, for as long as Spells can't damage it
  'aura-royal-ward': [
    { kind: 'glow', duration: 3.2, strength: 0.7, pulses: 1, size: 1.2 },
    { kind: 'halo', color: 2, delay: 0.3, duration: 2.6, size: 1.15, strength: 0.55, pulses: 1 },
    { kind: 'orbit', color: 1, duration: 3.2, count: 4, speed: 0.45, strength: 0.8 },
    { kind: 'sheen', color: 1, delay: 1.1, duration: 1.0, strength: 0.45 },
    { kind: 'twinkle', color: 1, delay: 0.5, count: 5, duration: 2.6, strength: 0.85, dot: 0.7 },
  ],
  // Taunt's chosen Unit: red rings pulse out from it, calling every attack its way, over a hot glow
  'aura-taunt': [
    { kind: 'glow', color: 2, duration: 2.4, strength: 0.55, pulses: 2, size: 1.1 },
    { kind: 'ripple', duration: 1.2, size: 2.2, strength: 0.75 },
    { kind: 'ripple', color: 1, delay: 0.8, duration: 1.2, size: 2.0, strength: 0.6 },
  ],
  // Givellt's Arcane Ascension: dark smoke curling round the card, violet arcane light swirling up through it,
  // and pale sparks
  'aura-ascended': [
    { kind: 'glow', color: 1, duration: 3, strength: 0.75, pulses: 1, size: 1.2 },
    { kind: 'wisp', dark: true, duration: 4, count: 6, size: 8, strength: 1.1, dot: 1.3 },
    { kind: 'spiral', color: 1, duration: 3.2, count: 4, size: 9, speed: 0.9, strength: 1.2, dot: 1.4 },
    { kind: 'twinkle', color: 2, delay: 0.6, count: 5, duration: 2.6, strength: 1, dot: 0.8 },
  ],
  // the Unit Jouster chose: steel and red rings close in on it like a target, over and over
  'aura-jousted': [
    { kind: 'halo', duration: 1.4, size: 1.45, strength: 0.75, pulses: 1 },
    { kind: 'halo', color: 1, delay: 0.25, duration: 1.2, size: 1.2, strength: 0.65, pulses: 1 },
    { kind: 'glow', color: 2, duration: 1.6, strength: 0.35, pulses: 1, size: 1.0 },
  ],
  // An Undead Guard (Grave Reinforcements) on the field: a soft blue ward glowing round its card without pause,
  // so both players can see it is taking every hit aimed at its lane
  'aura-bulwark': [
    { kind: 'glow', duration: 2.8, strength: 0.75, pulses: 1, size: 1.1 },
    { kind: 'glow', color: 1, delay: 0.5, duration: 2.0, strength: 0.35, pulses: 1, size: 0.9 },
  ],
  // a Pillar in every lane: the call is answered. A deep red eruption on its player's Emperor: the table shakes,
  // sparks are pulled in, a great column goes up and rings roll out across the mat
  pillarsCall: [
    { kind: 'shake', duration: 0.7, strength: 0.9 },
    { kind: 'flash', duration: 0.45, strength: 1.2 },
    { kind: 'gather', count: 30, duration: 0.9, size: 22 },
    { kind: 'gather', color: 2, dark: true, count: 18, duration: 0.9, size: 20 },
    { kind: 'pillar', delay: 0.7, duration: 2.6, size: 34, strength: 1.3 },
    { kind: 'shockwave', delay: 0.75, duration: 1.0, size: 40, strength: 1.1 },
    { kind: 'shockwave', color: 1, delay: 1.05, duration: 1.1, size: 30, strength: 0.9 },
    { kind: 'shockwave', delay: 1.4, duration: 1.2, size: 22, strength: 0.7 },
    { kind: 'riser', color: 1, delay: 0.8, count: 9, duration: 2.0, size: 26, strength: 1 },
    { kind: 'rise', color: 1, delay: 0.8, count: 30, duration: 2.4, size: 24, strength: 1 },
    { kind: 'orbit', color: 1, delay: 0.7, duration: 2.4, count: 5, speed: 1.6 },
    { kind: 'glow', delay: 0.6, duration: 2.8, strength: 1.2, pulses: 3, size: 1.8 },
  ],
  // …and each of the five Pillars answers at the same moment: its beacon flares into a tall column, sparks are
  // pulled in and a ring rolls out (smaller than the Emperor's, so five of them together don't drown the table)
  pillarAnswers: [
    { kind: 'flash', duration: 0.4, strength: 1.1 },
    { kind: 'gather', count: 14, duration: 0.8, size: 12 },
    { kind: 'gather', color: 2, dark: true, count: 8, duration: 0.8, size: 11 },
    { kind: 'pillar', delay: 0.6, duration: 2.8, size: 28, strength: 1.2 },
    { kind: 'shockwave', delay: 0.65, duration: 0.9, size: 16, strength: 0.9 },
    { kind: 'rise', color: 1, delay: 0.7, count: 14, duration: 2.4, size: 18, strength: 1 },
    { kind: 'glow', delay: 0.5, duration: 2.9, strength: 1.1, pulses: 3, size: 1.5 },
  ],
  // …and when the call is answered each Pillar cracks and bursts like a sacrificed unit (the 'sacrifice' effect),
  // with this on top: it glows hotter and hotter while it cracks, then goes up in a bright red blast of shards,
  // sparks and rings that hangs in the air a moment
  pillarShatter: [
    { kind: 'glow', duration: SACRIFICE_BURST, strength: 1.6, pulses: 4, size: 1.5 },
    { kind: 'rise', color: 1, count: 12, duration: SACRIFICE_BURST, size: 9, strength: 1 },
    { kind: 'flash', color: 1, delay: SACRIFICE_BURST, duration: 0.5, strength: 1.8 },
    { kind: 'shockwave', delay: SACRIFICE_BURST, duration: 0.9, size: 30, strength: 1.4 },
    { kind: 'shockwave', color: 1, delay: SACRIFICE_BURST + 0.15, duration: 0.9, size: 20, strength: 1.2 },
    { kind: 'shards', delay: SACRIFICE_BURST, duration: 1.5, count: 26, size: 18 },
    { kind: 'burst', color: 1, delay: SACRIFICE_BURST, count: 34, duration: 1.3, size: 16, strength: 1.4 },
    { kind: 'pillar', delay: SACRIFICE_BURST, duration: 1.4, size: 22, strength: 1.3 },
    { kind: 'glow', delay: SACRIFICE_BURST, duration: 1.6, strength: 1.3, pulses: 1, size: 2.0 },
  ],
  // The Remaining is played: a great deep red X slashes across Realm Beyond Comprehension, twice
  remainingArrives: [
    { kind: 'shake', duration: 0.5, strength: 0.7 },
    { kind: 'flash', duration: 0.35, strength: 1.2 },
    { kind: 'streak', duration: 0.55, count: 3, size: 3.4, strength: 1.4 },
    { kind: 'streak', flip: true, delay: 0.16, duration: 0.55, count: 3, size: 3.4, strength: 1.4 },
    { kind: 'streak', color: 1, delay: 0.5, duration: 0.5, count: 2, size: 2.8, strength: 1.2 },
    { kind: 'streak', color: 1, flip: true, delay: 0.64, duration: 0.5, count: 2, size: 2.8, strength: 1.2 },
    { kind: 'shockwave', delay: 0.3, duration: 0.8, size: 26, strength: 1.1 },
    { kind: 'burst', color: 1, delay: 0.3, count: 22, duration: 0.9, size: 12 },
    { kind: 'glow', delay: 0.1, duration: 1.6, strength: 1.1, pulses: 2, size: 1.6 },
  ],
  // the same pulse on a card whose Passive is switched off by a status (Entrenched, Stunned…): a little stronger, so the deep red reads
  passiveStopped: [
    { kind: 'glow', duration: 1.4, strength: 0.75, size: 0.9 },
    { kind: 'ripple', delay: 0.1, duration: 0.9, size: 1.5, strength: 0.6 },
  ],
  // ability "tap": a quick ring and a few sparks
  tap: [
    { kind: 'ripple', duration: 0.45, size: 1.6, strength: 0.85 },
    { kind: 'burst', color: 1, count: 8, duration: 0.6, size: 5 },
  ],
  // a Trigger springing (flipped face-up): purple sparks fly in from all around and sink into the card,
  // which swells with light as they arrive and answers with one flash and a ring: it has been activated
  trigger: [
    { kind: 'gather', count: 26, duration: 1.15, size: 14 },
    { kind: 'gather', color: 1, delay: 0.18, count: 16, duration: 1.05, size: 10 },
    { kind: 'glow', delay: 0.45, duration: 1.3, strength: 1.5, size: 1.1 },
    { kind: 'flash', color: 1, delay: 1.1, duration: 0.45, strength: 0.95 },
    { kind: 'ripple', color: 1, delay: 1.1, duration: 0.65, size: 2.1, strength: 0.85 },
  ],
  // a Trigger set face-down: a beam of purple light shoots up from the card as it lands…
  triggerSet: [
    { kind: 'flash', duration: 0.35, strength: 0.6 },
    { kind: 'ripple', duration: 0.7, size: 1.9, strength: 0.8 },
    { kind: 'pillar', duration: 1.6, size: 19, strength: 0.95 },
    { kind: 'rise', color: 1, delay: 0.05, count: 14, duration: 1.5, size: 12 },
  ],
  // …and while it waits, a slim beam stands on it with a few sparks drifting up. This one is played
  // again and again, each round fading in as the last fades out, so the beam looks steady.
  triggerAura: [
    { kind: 'beam', duration: 4, size: 12, strength: 0.85 },
    { kind: 'glow', duration: 4, strength: 1.0, size: 0.95 },
    { kind: 'rise', color: 1, count: 9, duration: 4, size: 9, strength: 0.85 },
  ],
  // a troop placed in the Minion slot: three quick flashes, a breathing aura and two rings
  minion: [
    { kind: 'flash', duration: 0.26, strength: 1.0 },
    { kind: 'flash', color: 1, delay: 0.30, duration: 0.26, strength: 0.85 },
    { kind: 'flash', delay: 0.60, duration: 0.30, strength: 0.7 },
    { kind: 'glow', duration: 1.5, strength: 1.5, pulses: 3, size: 1.12 },
    { kind: 'ripple', color: 1, duration: 0.6, size: 2.0, strength: 0.9 },
    { kind: 'ripple', delay: 0.32, duration: 0.6, size: 1.7, strength: 0.6 },
  ],
  // a unit sacrificed for Dark Gerzite: a dark maroon aura gathers while cracks spread across the card,
  // then it bursts into shards with a shockwave, and dark smoke hangs for a moment
  sacrifice: [
    { kind: 'glow', duration: 1.0, strength: 1.7, pulses: 3, size: 1.3, dark: true },
    { kind: 'glow', duration: 0.95, strength: 1.5, pulses: 3, size: 1.1 },
    { kind: 'cracks', duration: SACRIFICE_BURST, strength: 1 },
    { kind: 'shake', delay: SACRIFICE_BURST, duration: 0.3, strength: 0.3 },
    { kind: 'flash', color: 1, delay: SACRIFICE_BURST, duration: 0.32, strength: 1.4 },
    { kind: 'shockwave', color: 1, delay: SACRIFICE_BURST, duration: 0.55, size: 22, strength: 1.3 },
    { kind: 'shockwave', delay: SACRIFICE_BURST + 0.08, duration: 0.55, size: 14, strength: 1.2 },
    { kind: 'shards', delay: SACRIFICE_BURST, duration: 0.95, count: 18, size: 12 },
    { kind: 'burst', color: 1, delay: SACRIFICE_BURST, count: 20, duration: 0.8, size: 11, strength: 1.2 },
    { kind: 'glow', delay: SACRIFICE_BURST, duration: 1.0, strength: 1.3, size: 1.55, dark: true },
  ],
  // a card destroyed (its health is gone; not a sacrifice): it jolts, pale cracks run across it, and it shatters
  // into grey shards that drop away with a roll of dust and a single soft ring (no dark aura, no shockwaves)
  destroyed: [
    { kind: 'flash', duration: 0.18, strength: 0.9 },
    { kind: 'shake', duration: 0.18, strength: 0.25 },
    { kind: 'cracks', duration: SACRIFICE_BURST, strength: 1.1, line: 2 },
    { kind: 'glow', delay: 0.1, duration: SACRIFICE_BURST - 0.1, strength: 0.6, pulses: 2, size: 1.05 },
    { kind: 'flash', delay: SACRIFICE_BURST, duration: 0.25, strength: 1.1 },
    { kind: 'shards', color: 1, delay: SACRIFICE_BURST, duration: 1.2, count: 24, size: 8 },
    { kind: 'dust', color: 1, delay: SACRIFICE_BURST, count: 12, duration: 1.4, size: 7, strength: 0.85, dot: 3.6 },
    { kind: 'fall', delay: SACRIFICE_BURST, count: 14, duration: 1.1, size: 4, strength: 0.9, dot: 0.6 },
    { kind: 'ripple', delay: SACRIFICE_BURST, duration: 0.7, size: 2.2, strength: 0.7 },
  ],
  // a card played as a Darkgale summon: a dark red swell around the card, a ring, and embers rising off it
  darkgale: [
    { kind: 'glow', duration: 1.4, strength: 1.7, pulses: 2, size: 1.3, dark: true },
    { kind: 'glow', color: 1, delay: 0.1, duration: 1.2, strength: 1.2, size: 1.1 },
    { kind: 'ripple', color: 1, duration: 0.7, size: 2.2, strength: 0.9 },
    { kind: 'rise', color: 1, delay: 0.1, count: 18, duration: 1.5, size: 8 },
  ],
  // a status token doing its work: a ring or two spreading from the token itself
  statusDamage: [
    { kind: 'shockwave', duration: 0.55, size: 9, strength: 1.2 },
    { kind: 'shockwave', color: 1, delay: 0.1, duration: 0.5, size: 6, strength: 0.9 },
  ],
  statusHeal: [
    { kind: 'shockwave', duration: 0.6, size: 9, strength: 1.1 },
    { kind: 'shockwave', color: 1, delay: 0.12, duration: 0.55, size: 6, strength: 0.9 },
  ],
  // "your move": three slower rings, so it catches the eye
  statusNotice: [
    { kind: 'shockwave', duration: 0.7, size: 11, strength: 1.3 },
    { kind: 'shockwave', color: 1, delay: 0.45, duration: 0.7, size: 11, strength: 1.2 },
    { kind: 'shockwave', delay: 0.9, duration: 0.7, size: 11, strength: 1.1 },
  ],
  // healing on a card: a soft green breath and sparks drifting up
  heal: [
    { kind: 'glow', duration: 1.2, strength: 0.9, size: 1.0 },
    { kind: 'rise', color: 1, count: 12, duration: 1.2, size: 6 },
  ],
  // a hit: a flash on the card, a small red shockwave and a few sparks
  damage: [
    { kind: 'flash', duration: 0.3, strength: 0.9 },
    { kind: 'shockwave', duration: 0.45, size: 9, strength: 0.9 },
    { kind: 'burst', color: 1, count: 9, duration: 0.5, size: 5 },
  ],
  // swift: three quick slashes of light across the card and a fast ring
  ambush: [
    { kind: 'streak', duration: 0.42, count: 3, size: 1.6 },
    { kind: 'streak', color: 1, delay: 0.12, duration: 0.36, count: 2, size: 1.3 },
    { kind: 'ripple', color: 1, delay: 0.1, duration: 0.4, size: 1.9, strength: 0.9 },
  ],
  emperor: [
    { kind: 'flash', duration: 0.3, strength: 1.2 },
    { kind: 'ripple', color: 1, duration: 0.7, size: 2.6 },
    { kind: 'ripple', delay: 0.12, duration: 0.75, size: 3.2 },
    { kind: 'orbit', color: 1, duration: 0.9, count: 2, speed: 2.2 },
    { kind: 'burst', delay: 0.05, count: 16, duration: 0.9, size: 8 },
    { kind: 'burst', color: 1, delay: 0.1, count: 12, duration: 1.0, size: 7 },
  ],
};

// ---------------- a card going straight from one pile to the other ----------------
Object.assign(RECIPES, {
  // the pile it leaves: a ring runs out from it and a few sparks lift off
  pileOut: [
    { kind: 'ripple', duration: 0.6, size: 1.7, strength: 0.9 },
    { kind: 'rise', color: 1, count: 8, duration: 0.9, size: 5, strength: 0.9 },
  ],
  // the pile it lands on: sparks are drawn in, then a flash, a ring and two trails racing round it
  pileIn: [
    { kind: 'gather', color: 1, count: 12, duration: 0.55, size: 8 },
    { kind: 'flash', delay: 0.4, duration: 0.4, strength: 0.9 },
    { kind: 'ripple', delay: 0.4, duration: 0.7, size: 2.0, strength: 0.95 },
    { kind: 'orbit', color: 1, delay: 0.35, duration: 0.9, count: 2, speed: 2.2 },
  ],
} satisfies Record<string, Layer[]>);

// ---------------- affiliation auras ----------------
// The small scene that plays above an active Biome or Tome every so often, one per affiliation
// ("aura-" + the affiliation's name). They are meant to be noticed, not watched: few particles, low
// strength, about three seconds. Colors are in src/config.ts (playEffects.colors).
Object.assign(RECIPES, {
  // embers lift off the card while a little dark smoke rolls up
  'aura-volcanic': [
    { kind: 'puff', color: 1, dark: true, count: 5, duration: 3.4, size: 6.5, strength: 1, dot: 3.4 },
    { kind: 'rise', count: 7, duration: 2.8, size: 5.5, strength: 0.9 },
    { kind: 'glow', duration: 2.6, strength: 0.35, size: 0.8 },
  ],
  // a whip of sand blows across, then a thinner one behind it
  'aura-duned': [
    { kind: 'drift', count: 6, duration: 2.6, size: 10, strength: 0.95, dot: 3.6 },
    { kind: 'drift', color: 1, delay: 0.5, count: 4, duration: 2.4, size: 9, strength: 0.6, dot: 2.4 },
  ],
  // snow falls, with a glint or two of frost
  'aura-frigid': [
    { kind: 'fall', count: 10, duration: 3.4, size: 5, strength: 0.9, dot: 0.5 },
    { kind: 'twinkle', color: 1, count: 4, duration: 3, strength: 0.8, dot: 0.9 },
  ],
  // a few leaves drift down among fireflies
  'aura-forested': [
    { kind: 'fall', count: 5, duration: 3.6, size: 4.5, strength: 0.85, dot: 0.85 },
    { kind: 'twinkle', color: 1, count: 5, duration: 3.4, strength: 0.8, dot: 0.6 },
  ],
  // bubbles rise over two slow ripples
  'aura-submerged': [
    { kind: 'rise', color: 1, count: 7, duration: 3.2, size: 4.5, strength: 0.7 },
    { kind: 'ripple', duration: 1.6, size: 1.5, strength: 0.4 },
    { kind: 'ripple', delay: 1.1, duration: 1.6, size: 1.5, strength: 0.35 },
  ],
  // a sickly green mist seeps up and blackens as it rises, carrying pale motes
  'aura-undead': [
    { kind: 'puff', toBlack: true, count: 4, duration: 3.6, size: 4.5, strength: 0.9, dot: 3 },
    { kind: 'rise', color: 1, count: 6, duration: 3, size: 4, strength: 0.8 },
  ],
  // two pale wisps circle the card through a thin haze
  'aura-spectral': [
    { kind: 'orbit', count: 2, duration: 3.2, speed: 0.45, strength: 0.6 },
    { kind: 'puff', color: 1, count: 2, duration: 3.2, size: 4, strength: 0.4, dot: 2.8 },
  ],
  // arcane sparkles wink in and out
  'aura-magical': [
    { kind: 'twinkle', count: 7, duration: 3, strength: 0.9, dot: 1.0 },
    { kind: 'twinkle', color: 1, delay: 0.4, count: 4, duration: 2.6, strength: 0.8, dot: 0.7 },
  ],
  // a slow dark swirl of purple and grey cloud, with starlight winking through it
  'aura-astral': [
    { kind: 'orbit', count: 3, duration: 3.4, speed: 0.35, strength: 0.65 },
    { kind: 'puff', color: 1, count: 2, duration: 3.4, size: 4.5, strength: 0.45, dot: 2.6 },
    { kind: 'twinkle', color: 2, delay: 0.3, count: 5, duration: 3, strength: 0.85, dot: 0.7 },
  ],
  // an Astral Biome: a small, unknown planetary system turning slowly above it, with starlight winking round it
  'aura-planets': [
    { kind: 'planets', duration: 8, count: 4, size: 4.2, strength: 0.95 },
    { kind: 'twinkle', color: 4, delay: 0.5, count: 6, duration: 7, size: 11, strength: 0.7, dot: 0.6 },
  ],
  // a soft light with a few glints
  'aura-neutral': [
    { kind: 'twinkle', count: 5, duration: 3, strength: 0.85, dot: 0.95 },
    { kind: 'glow', duration: 2.8, strength: 0.4, size: 0.8 },
  ],
} satisfies Record<string, Layer[]>);

// ---------------- the Epic and Mythic Tomes' own auras ----------------
// Each of these Tomes has a scene of its own instead of its affiliation's (abilityEffects.ambient.byCard in
// src/config.ts says which card plays which, and how often). Colors are in playEffects.colors.
Object.assign(RECIPES, {
  // Blood-Drawing Tome: now and then blood wells up on the card and runs down it in trails, and the table
  // around it darkens a little
  'aura-blood-drawing': [
    { kind: 'glow', color: 2, dark: true, duration: 3.6, strength: 0.75, size: 0.95 },
    { kind: 'seep', duration: 3.6, count: 4, size: 4.2, dot: 2.1, strength: 1 },
  ],
  // Forbidden Tome: dark wisps curl up off it without pause, over a faint purple pulse and a few pale motes.
  // Its scenes overlap (a negative gap in config.ts), so it never goes still.
  'aura-forbidden': [
    { kind: 'wisp', dark: true, duration: 4.5, count: 5, size: 7, strength: 1, dot: 1.1 },
    { kind: 'glow', color: 1, duration: 4.5, strength: 0.32, pulses: 1, size: 1.0 },
    { kind: 'rise', color: 2, delay: 0.6, count: 3, duration: 3.6, size: 5, strength: 0.5 },
  ],
  // Tome of Gerzite: a gleam of light blue sweeps across it, and glints flash over it in its wake
  'aura-gerzite': [
    { kind: 'sheen', duration: 1.1, strength: 0.7 },
    { kind: 'twinkle', color: 1, delay: 0.25, count: 4, duration: 1.8, strength: 1, dot: 1.4 },
    { kind: 'twinkle', color: 2, delay: 0.5, count: 3, duration: 1.6, strength: 0.9, dot: 0.9 },
  ],
  // Self-Destructing Tome: every so often it shakes and rumbles, glowing hot, shedding embers and a puff of smoke
  'aura-self-destructing': [
    { kind: 'rumble', duration: 1.1, strength: 1 },
    { kind: 'glow', duration: 1.4, strength: 0.55, pulses: 2, size: 1.0 },
    { kind: 'puff', color: 2, dark: true, delay: 0.2, count: 3, duration: 2.2, size: 4, strength: 0.7, dot: 2.4 },
    { kind: 'rise', color: 1, delay: 0.1, count: 6, duration: 1.6, size: 4, strength: 0.85 },
  ],
  // Self-Destructing Tome, the moment its timer runs out: the sacrifice burst, in orange. It shakes harder
  // and harder while it cracks, then blows apart with a flash, two shockwaves, shards, sparks and smoke.
  // (The card itself stays on the table, marked Destroyed, so it can still be restarted or cleared away.)
  'self-destruct': [
    { kind: 'rumble', duration: SACRIFICE_BURST, strength: 2.4 },
    { kind: 'glow', duration: 1.0, strength: 1.7, pulses: 3, size: 1.3, dark: true },
    { kind: 'glow', duration: 0.95, strength: 1.5, pulses: 3, size: 1.1 },
    { kind: 'cracks', duration: SACRIFICE_BURST, strength: 1 },
    { kind: 'shake', delay: SACRIFICE_BURST, duration: 0.4, strength: 0.45 },
    { kind: 'flash', color: 1, delay: SACRIFICE_BURST, duration: 0.36, strength: 1.6 },
    { kind: 'shockwave', color: 1, delay: SACRIFICE_BURST, duration: 0.6, size: 26, strength: 1.4 },
    { kind: 'shockwave', delay: SACRIFICE_BURST + 0.08, duration: 0.6, size: 16, strength: 1.2 },
    { kind: 'shards', delay: SACRIFICE_BURST, duration: 0.95, count: 20, size: 13 },
    { kind: 'burst', color: 1, delay: SACRIFICE_BURST, count: 24, duration: 0.85, size: 12, strength: 1.3 },
    { kind: 'puff', color: 2, dark: true, delay: SACRIFICE_BURST + 0.05, count: 5, duration: 2, size: 6, strength: 0.9, dot: 4 },
    { kind: 'glow', delay: SACRIFICE_BURST, duration: 1.0, strength: 1.3, size: 1.55, dark: true },
  ],
  // Haste Tome: blue and light pink streaks race across just above it
  'aura-haste': [
    { kind: 'glide', duration: 1.1, count: 4, size: 2.2, strength: 0.9 },
    { kind: 'glide', color: 1, delay: 0.18, duration: 1.0, count: 3, size: 2, strength: 0.85 },
  ],
  // Tome of Greed: purple, grey and black sparks are pulled in from all around and swallowed by it
  'aura-greed': [
    { kind: 'gather', duration: 1.3, count: 10, size: 9, strength: 0.95 },
    { kind: 'gather', color: 1, delay: 0.1, duration: 1.25, count: 7, size: 8.5, strength: 0.7 },
    { kind: 'gather', color: 2, dark: true, delay: 0.18, duration: 1.2, count: 8, size: 8.5, strength: 0.9 },
    { kind: 'glow', delay: 0.85, duration: 0.8, strength: 0.6, size: 0.95 },
  ],
  // Moldering Tome: a quick black pulse (a dark swell and a dark ring), then red and pink risers shoot up
  // off it over a warm red glow
  'aura-moldering': [
    { kind: 'glow', color: 2, dark: true, duration: 0.5, strength: 1.6, size: 0.8 },
    { kind: 'ripple', color: 2, dark: true, duration: 0.5, size: 1.7, strength: 0.9 },
    { kind: 'riser', delay: 0.35, duration: 1.8, count: 9, size: 7, strength: 0.95 },
    { kind: 'riser', color: 1, delay: 0.6, duration: 1.6, count: 6, size: 6, strength: 0.9 },
    { kind: 'glow', delay: 0.35, duration: 1.8, strength: 0.45, size: 0.95 },
  ],
  // Surging Power Tome: forks of lightning leap off its edges, with sparks and a flickering glow
  'aura-surging': [
    { kind: 'bolt', duration: 1.4, count: 3, size: 4.5, strength: 1 },
    { kind: 'bolt', color: 2, delay: 0.3, duration: 1.1, count: 2, size: 3.5, strength: 0.9 },
    { kind: 'rise', color: 1, count: 7, duration: 0.8, size: 3, strength: 0.95 },
    { kind: 'rise', delay: 0.55, count: 6, duration: 0.8, size: 3, strength: 0.85 },
    { kind: 'glow', duration: 1.4, strength: 0.35, pulses: 3, size: 0.95 },
  ],
} satisfies Record<string, Layer[]>);

// ---------------- the Ancients' own effects ----------------
// Each belongs to one Ancient card (CONFIG.looks and abilityEffects.ambient.byCard in src/config.ts say which
// plays when). Colors are in playEffects.colors.
/** The sacrifice burst (cracks, then the card bursts apart), in other colors: a card that goes its own way. */
const burstIn = (extra: Layer[] = [], shards = 0, sparks = 1): Layer[] => [
  { kind: 'glow', duration: 1.0, strength: 1.5, pulses: 3, size: 1.3 },
  { kind: 'cracks', duration: SACRIFICE_BURST, strength: 1 },
  { kind: 'shake', delay: SACRIFICE_BURST, duration: 0.3, strength: 0.3 },
  { kind: 'flash', color: 1, delay: SACRIFICE_BURST, duration: 0.32, strength: 1.4 },
  { kind: 'shockwave', color: 1, delay: SACRIFICE_BURST, duration: 0.55, size: 22, strength: 1.3 },
  { kind: 'shockwave', delay: SACRIFICE_BURST + 0.08, duration: 0.55, size: 14, strength: 1.2 },
  { kind: 'shards', color: shards, delay: SACRIFICE_BURST, duration: 0.95, count: 18, size: 12 },
  { kind: 'burst', color: sparks, delay: SACRIFICE_BURST, count: 22, duration: 0.8, size: 11, strength: 1.2 },
  { kind: 'glow', color: 1, delay: SACRIFICE_BURST, duration: 1.0, strength: 1.1, size: 1.55 },
  ...extra,
];
/** Twilight Seedling's aura, step 1 … 5 as it grows: more motes and fireflies, spread wider. */
/** Cragged Skulker's aura: a water geyser round its edge for each attacking action it has gained (1 … 8). */
const skulker = (n: number): Layer[] => [{ kind: 'geyser', duration: 3, count: n, size: 5.5, strength: 1.4 }, { kind: 'geyser', color: 1, duration: 3, count: n, size: 4, strength: 0.9 }];
// Marlin Knight: a soft sea-blue glow round it, stronger for each extra hit it has saved up (1 … 6)
const marlin = (n: number): Layer[] => [
  { kind: 'glow', duration: 2.6, strength: 0.4 + n * 0.14, size: 0.85 + n * 0.05 },
  ...(n >= 3 ? [{ kind: 'twinkle', color: 1, duration: 2.4, count: n - 1, dot: 0.7, size: 2.5, strength: 0.5 + n * 0.06 } as Layer] : []),
];
const seedling = (n: number): Layer[] => [
  { kind: 'fireflies', color: 2, count: 4 + n * 3, duration: 3.6, strength: 1, dot: 0.75 + n * 0.06, size: 4 + n * 2 },
  { kind: 'fireflies', color: 1, count: 3 + n * 3, duration: 3.6, strength: 0.9, dot: 0.6 + n * 0.05, size: 3.5 + n * 2 },
  { kind: 'fireflies', count: 3 + n * 2, duration: 3.6, strength: 1, dot: 0.8 + n * 0.08, size: 3 + n * 1.8 },
  { kind: 'glow', duration: 3.6, strength: 0.25 + n * 0.08, size: 0.85 + n * 0.12 },
  ...(n >= 3 ? [{ kind: 'orbit', color: 1, duration: 3.6, count: n - 2, speed: 0.35, strength: 0.5 } as Layer] : []),
];
Object.assign(RECIPES, {
  // Archmage looks at the opponent's deck: tongues of fire dance round it, with embers and a hot glow
  'archmage-fire': [
    { kind: 'flames', duration: 2.8, count: 16, size: 3, speed: 0.35, strength: 1 },
    { kind: 'flames', color: 2, delay: 0.2, duration: 2.5, count: 10, size: 2, speed: -0.5, strength: 0.9 },
    { kind: 'orbit', color: 1, duration: 2.6, count: 3, speed: 1.1, strength: 0.9 },
    { kind: 'rise', color: 1, count: 16, duration: 2.4, size: 7 },
    { kind: 'glow', duration: 2.8, strength: 0.75, pulses: 3, size: 1.15 },
  ],
  // …and a Submerged card sent to the bottom of the opponent's deck: steam rising off the deck
  'archmage-steam': [
    { kind: 'puff', count: 6, duration: 2.8, size: 9, strength: 0.6, dot: 3.4 },
    { kind: 'puff', color: 1, delay: 0.4, count: 5, duration: 2.6, size: 11, strength: 0.45, dot: 4.2 },
    { kind: 'wisp', delay: 0.15, count: 3, duration: 2.6, size: 9, strength: 0.6, dot: 1.4 },
  ],
  // Clay Soldiers as one big Troop: a big impact cloud of sand and dust rolls out from it
  'clay-big': [
    { kind: 'shake', duration: 0.45, strength: 0.5 },
    { kind: 'dust', count: 24, duration: 2.2, size: 16, strength: 1, dot: 7.5 },
    { kind: 'dust', color: 1, delay: 0.05, count: 16, duration: 1.9, size: 11, strength: 1, dot: 5.5 },
    { kind: 'dust', color: 2, delay: 0.1, count: 10, duration: 1.6, size: 7, strength: 0.8, dot: 4 },
    { kind: 'puff', color: 2, count: 7, duration: 2.4, size: 7, strength: 0.75, dot: 6.5 },
    { kind: 'burst', color: 2, count: 26, duration: 0.9, size: 10, strength: 0.9 },
    { kind: 'shockwave', color: 2, duration: 0.6, size: 18, strength: 0.6 },
  ],
  // …as small units (or a Lanespanner's): a small puff of sand on each
  'clay-small': [
    { kind: 'dust', count: 10, duration: 1.4, size: 6, strength: 1, dot: 3.4 },
    { kind: 'burst', color: 2, count: 9, duration: 0.7, size: 4.5, strength: 0.8 },
    { kind: 'puff', color: 1, count: 2, duration: 1.3, size: 3, strength: 0.5, dot: 2.4 },
  ],
  // a card drawn by Cosmic Beyonder, flying to the hand: sparkles about it as it goes
  cosmic: [
    { kind: 'twinkle', count: 14, duration: 1.1, strength: 1.2, dot: 2, size: 6 },
    { kind: 'twinkle', color: 2, delay: 0.1, count: 10, duration: 1.0, strength: 1.2, dot: 1.6, size: 6 },
    { kind: 'twinkle', color: 1, delay: 0.2, count: 8, duration: 0.9, strength: 1.2, dot: 1.2, size: 5 },
    { kind: 'orbit', color: 1, duration: 1.0, count: 2, speed: 1.8, strength: 1 },
  ],
  // Cragged Skulker gains Damage Boost: a pulse
  'skulker-pulse': [
    { kind: 'glow', duration: 0.9, strength: 1.2, size: 1.1 },
    { kind: 'ripple', duration: 0.7, size: 2.1, strength: 0.9 },
    { kind: 'ripple', color: 1, delay: 0.15, duration: 0.6, size: 1.7, strength: 0.7 },
  ],
  // Cursed Sarcophagus: golden glimmers wink above it, without end
  'aura-sarcophagus': [
    { kind: 'twinkle', count: 9, duration: 3.2, strength: 1.1, dot: 1.6, size: 7 },
    { kind: 'twinkle', color: 1, delay: 0.5, count: 6, duration: 2.8, strength: 1, dot: 1.0, size: 6 },
    { kind: 'rise', count: 4, duration: 3, size: 5, strength: 0.5 },
  ],
  // …and when it breaks open for the Hexed Mummy, it bursts like a sacrifice, in gold, golden sparks drifting up after
  'burst-gold': burstIn([{ kind: 'rise', color: 1, delay: SACRIFICE_BURST, count: 22, duration: 1.8, size: 12 }]),
  // Gerzite Titan bursts into its Clusters: purple, grey and light blue shards and sparks
  'burst-gerzite': burstIn([
    { kind: 'shards', color: 1, delay: SACRIFICE_BURST, duration: 1.0, count: 12, size: 14 },
    { kind: 'burst', color: 2, delay: SACRIFICE_BURST + 0.05, count: 18, duration: 0.9, size: 13, strength: 1.2 },
    { kind: 'shockwave', color: 2, delay: SACRIFICE_BURST + 0.15, duration: 0.6, size: 18, strength: 1 },
  ], 2, 0),
  // Eldritch Realm Crosser while it is a Biome: spectral swirls winding up off it, without pause
  'aura-crosser': [
    { kind: 'spiral', duration: 3.6, count: 5, size: 10, speed: 1.3, strength: 1.3, dot: 1.8 },
    { kind: 'spiral', color: 1, delay: 0.5, duration: 3.2, count: 4, size: 8, speed: -1.1, strength: 1.2, dot: 1.5 },
    { kind: 'wisp', color: 1, delay: 0.2, duration: 3.4, count: 3, size: 8, strength: 0.8, dot: 1.4 },
    { kind: 'riser', color: 2, delay: 0.3, duration: 2.8, count: 6, size: 9, strength: 0.8 },
    { kind: 'glow', color: 1, duration: 3.6, strength: 0.55, pulses: 1, size: 1.15 },
  ],
  // a Troop sacrificed to the Fleshstealer: the red that ran across from it soaks into the Fleshstealer
  'flesh-blood': [
    { kind: 'gather', count: 16, duration: 0.8, size: 6 },
    { kind: 'glow', delay: 0.4, duration: 1.1, strength: 1.3, pulses: 2, size: 1.15 },
    { kind: 'ripple', color: 1, delay: 0.6, duration: 0.6, size: 1.8, strength: 0.8 },
  ],
  // Forest Hivemind gains stats: a green glow, a ring and sparks lifting off
  'hive-up': [
    { kind: 'glow', duration: 1.2, strength: 1.1, size: 1.1 },
    { kind: 'ripple', duration: 0.7, size: 1.9, strength: 0.8 },
    { kind: 'rise', color: 1, count: 12, duration: 1.2, size: 6 },
  ],
  // …loses them: a dull brown glow, a dark ring closing in and motes sinking
  'hive-down': [
    { kind: 'glow', dark: true, duration: 1.2, strength: 1.4, size: 1.1 },
    { kind: 'glow', duration: 1.0, strength: 0.6, size: 0.95 },
    { kind: 'gather', color: 1, dark: true, count: 12, duration: 0.9, size: 6 },
    { kind: 'fall', count: 8, duration: 1.2, size: 4, strength: 0.8, dot: 0.6 },
  ],
  // Frozen Scarecrow calls an Icewing: birds wheel round it
  'scarecrow-birds': [
    { kind: 'flock', dark: true, duration: 3.6, count: 7, size: 6.5, dot: 2, speed: 0.32 },
    { kind: 'flock', color: 1, dark: true, delay: 0.3, duration: 3.2, count: 4, size: 4.5, dot: 1.5, speed: -0.42 },
  ],
  // Great Hay Golem throws damage back: a cloud of straw bursts off it with a flash and a ring
  'golem-reflect': [
    { kind: 'puff', count: 5, duration: 1.4, size: 3.5, strength: 0.75, dot: 3.2 },
    { kind: 'burst', count: 24, duration: 0.9, size: 8, strength: 1 },
    { kind: 'burst', color: 2, count: 14, duration: 1.1, size: 6, strength: 0.9 },
    { kind: 'flash', color: 1, duration: 0.35, strength: 1.2 },
    { kind: 'shockwave', color: 1, duration: 0.55, size: 14, strength: 1 },
  ],
  // Arcane Doors: light blue doors of light swing open over the card, with light spilling out between them
  'arcane-doors': [
    { kind: 'doors', duration: 1.6, strength: 1 },
    { kind: 'twinkle', color: 1, delay: 0.4, count: 6, duration: 1.1, strength: 0.9, dot: 0.6 },
  ],
  // All Stop: a pulse rolls out from the card across the whole table…
  // Tornado: a swirling funnel over the lane it strikes, with a ring of wind round the lanes it reaches
  // (the ring's width comes from the table: the lanes it reaches)
  tornado: [
    { kind: 'twister', duration: 2.4, count: 46, strength: 1 },
    { kind: 'dust', color: 2, count: 14, duration: 1.8, size: 10, strength: 0.7, dot: 3 },
    { kind: 'shake', delay: 0.2, duration: 0.6, strength: 0.25 },
  ],
  'allstop-pulse': [
    { kind: 'flash', color: 1, duration: 0.3, strength: 1.2 },
    { kind: 'shockwave', duration: 1.4, size: 260, strength: 1.1 },
    { kind: 'shockwave', color: 1, delay: 0.12, duration: 1.4, size: 240, strength: 0.7 },
    { kind: 'shake', duration: 0.3, strength: 0.3 },
  ],
  // …and each Spell it reaches bursts apart
  'allstop-burst': [
    { kind: 'flash', color: 1, duration: 0.25, strength: 1.2 },
    { kind: 'shockwave', duration: 0.5, size: 14, strength: 1 },
    { kind: 'shards', duration: 0.9, count: 18, size: 9 },
    { kind: 'burst', color: 1, count: 14, duration: 0.7, size: 8, strength: 1 },
  ],
  // Dark Bargain: each Gerzite lost rises off the bar and shatters
  'gz-shatter': [
    { kind: 'rise', count: 4, duration: 0.8, size: 4 },
    { kind: 'flash', delay: 0.45, duration: 0.25, strength: 0.9 },
    { kind: 'shards', color: 1, delay: 0.45, duration: 0.8, count: 10, size: 4 },
  ],
  // a coin flip won: a pulse on the coin (Magical Coin purple, Devil's Coin red)
  'coin-win-magical': [
    { kind: 'glow', duration: 1.2, size: 1.3, strength: 1.2, pulses: 2 },
    { kind: 'shockwave', delay: 0.1, duration: 0.7, size: 18, strength: 1 },
    { kind: 'shockwave', color: 1, delay: 0.35, duration: 0.7, size: 14, strength: 0.8 },
  ],
  'coin-win-devil': [
    { kind: 'glow', duration: 1.2, size: 1.3, strength: 1.3, pulses: 2 },
    { kind: 'shockwave', delay: 0.1, duration: 0.7, size: 18, strength: 1 },
    { kind: 'rise', color: 1, delay: 0.1, count: 12, duration: 1.1, size: 6 },
  ],
  // a coin flip lost: the coin rises off the table, then shatters like a destroyed card
  'coin-shatter': [
    { kind: 'lift', duration: 0.7, size: 5 },
    { kind: 'cracks', duration: 0.7, strength: 1.1, line: 2 },
    { kind: 'flash', delay: 0.7, duration: 0.25, strength: 1.1 },
    { kind: 'shards', color: 1, delay: 0.7, duration: 1.2, count: 24, size: 8 },
    { kind: 'dust', color: 1, delay: 0.7, count: 10, duration: 1.2, size: 6, strength: 0.8, dot: 3.4 },
  ],
  // Timed Blast going off over its lane: a spectral flash, rings of pale blue and violet, and sparks thrown out
  'timed-blast': [
    { kind: 'flash', color: 2, duration: 0.35, strength: 1.2 },
    { kind: 'pillar', duration: 0.9, size: 12, strength: 0.9 },
    { kind: 'shockwave', duration: 0.7, size: 18, strength: 1 },
    { kind: 'shockwave', color: 1, delay: 0.12, duration: 0.8, size: 24, strength: 0.8 },
    { kind: 'burst', color: 1, count: 22, duration: 0.9, size: 9, strength: 1 },
    { kind: 'shake', duration: 0.4, strength: 0.45 },
  ],
  // Crystalline Discharge: a great ringed burst over each lane, light blue and purple rings one after another
  'crystal-burst': [
    { kind: 'flash', color: 2, duration: 0.4, strength: 1.3 },
    { kind: 'shockwave', duration: 0.7, size: 16, strength: 1.1 },
    { kind: 'shockwave', color: 1, delay: 0.1, duration: 0.8, size: 24, strength: 1 },
    { kind: 'shockwave', delay: 0.22, duration: 0.9, size: 32, strength: 0.85 },
    { kind: 'shockwave', color: 1, delay: 0.34, duration: 1, size: 40, strength: 0.7 },
    { kind: 'pillar', duration: 1, size: 14, strength: 1 },
    { kind: 'burst', count: 26, duration: 1, size: 11, strength: 1 },
    { kind: 'burst', color: 1, count: 16, delay: 0.15, duration: 1, size: 8, strength: 0.9 },
    { kind: 'shake', delay: 0.05, duration: 0.5, strength: 0.6 },
  ],
  // Gerzite paid into Gerzite Dagger / Crystalline Discharge: a light blue glow round the card, bigger and brighter
  // the more Gerzite went into it (1–3, 4–7, 8 or more)
  'gz-charge-1': [
    { kind: 'glow', duration: 1.4, size: 0.9, strength: 0.7 },
    { kind: 'twinkle', color: 1, count: 4, duration: 1.4, strength: 0.8, dot: 0.6 },
  ],
  'gz-charge-2': [
    { kind: 'glow', duration: 1.6, size: 1.25, strength: 0.95, pulses: 2 },
    { kind: 'halo', duration: 1.6, size: 1.1, strength: 0.8, pulses: 2 },
    { kind: 'twinkle', color: 1, count: 8, duration: 1.6, strength: 1, dot: 0.7 },
  ],
  'gz-charge-3': [
    { kind: 'glow', duration: 1.8, size: 1.7, strength: 1.2, pulses: 3 },
    { kind: 'halo', duration: 1.8, size: 1.35, strength: 1, pulses: 3 },
    { kind: 'orbit', count: 3, duration: 1.8, strength: 1 },
    { kind: 'twinkle', color: 1, count: 12, duration: 1.8, strength: 1.1, dot: 0.8 },
    { kind: 'rumble', duration: 1.8, strength: 0.25 },
  ],
  // Summit Tarragon's discount: a cold gust swirls round the Tarragon as it blows, and snow settles over the card it
  // reaches, with an icy sheen across it
  'summit-gust': [
    { kind: 'drift', count: 5, duration: 1.6, size: 8, strength: 0.7, dot: 3 },
    { kind: 'fall', color: 1, count: 14, duration: 1.4, size: 5, strength: 1, dot: 0.6 },
    { kind: 'sheen', color: 2, delay: 0.2, duration: 0.7, strength: 0.8 },
    { kind: 'puff', color: 2, delay: 0.1, count: 4, duration: 1.2, size: 5, strength: 0.6, dot: 2.6 },
  ],
  // Executioner's axe comes back to its lane: a great cleave across it, a red flash and a shockwave of dust
  'axe-return': [
    { kind: 'slash', duration: 0.55, size: 1.6, dot: 2.6, color: 1, strength: 1.3 },
    { kind: 'slash', duration: 0.55, size: 1.5, dot: 0.8, strength: 1.8 },
    { kind: 'flash', color: 1, delay: 0.12, duration: 0.3, strength: 1 },
    { kind: 'shockwave', color: 1, delay: 0.12, duration: 0.6, size: 16, strength: 0.9 },
    { kind: 'dust', delay: 0.15, duration: 1.1, count: 10, size: 6, strength: 0.7 },
    { kind: 'shake', delay: 0.12, duration: 0.35, strength: 0.35 },
  ],
  // Highcard Assassin cuts a high card: an X slashes across it, twice
  'assassin-x': [
    { kind: 'slash', duration: 0.6, size: 1.2, dot: 2.2, color: 1, strength: 1.2 },
    { kind: 'slash', duration: 0.6, size: 1.15, dot: 0.6, strength: 1.6 },
    { kind: 'slash', flip: true, delay: 0.14, duration: 0.6, size: 1.2, dot: 2.2, color: 1, strength: 1.2 },
    { kind: 'slash', flip: true, delay: 0.14, duration: 0.6, size: 1.15, dot: 0.6, strength: 1.6 },
    { kind: 'flash', color: 1, delay: 0.14, duration: 0.25, strength: 0.8 },
    { kind: 'slash', delay: 0.55, duration: 0.6, size: 1.3, dot: 2.4, color: 1, strength: 1.3 },
    { kind: 'slash', delay: 0.55, duration: 0.6, size: 1.25, dot: 0.7, strength: 1.7 },
    { kind: 'slash', flip: true, delay: 0.69, duration: 0.6, size: 1.3, dot: 2.4, color: 1, strength: 1.3 },
    { kind: 'slash', flip: true, delay: 0.69, duration: 0.6, size: 1.25, dot: 0.7, strength: 1.7 },
    { kind: 'flash', color: 1, delay: 0.69, duration: 0.3, strength: 1 },
    { kind: 'burst', color: 1, delay: 0.69, count: 12, duration: 0.6, size: 5 },
  ],
  // Leviathan with Body Segments out: water rippling out round it every few seconds
  'aura-leviathan': [
    { kind: 'ripple', duration: 1.8, size: 2.6, strength: 0.65 },
    { kind: 'ripple', color: 1, delay: 0.45, duration: 1.7, size: 2.2, strength: 0.45 },
    { kind: 'ripple', delay: 0.9, duration: 1.6, size: 1.8, strength: 0.35 },
  ],
  // Occult Sorcerer's discount used: each end of the purple beam flares
  'occult-beam': [
    { kind: 'glow', duration: 1.1, strength: 1.2, size: 1.1 },
    { kind: 'ripple', duration: 0.6, size: 1.8, strength: 0.8 },
    { kind: 'twinkle', color: 1, count: 5, duration: 1, strength: 0.9, dot: 0.9, size: 6 },
  ],
  // Primordial Incinerator, untapped: small flames lick round it, with embers and a little smoke
  'aura-incinerator': [
    { kind: 'flames', duration: 2.6, count: 14, size: 2.6, strength: 1 },
    { kind: 'flames', color: 1, delay: 0.3, duration: 2.2, count: 8, size: 1.6, strength: 0.9 },
    { kind: 'rise', count: 7, duration: 2.4, size: 6, strength: 0.9 },
    { kind: 'puff', color: 2, dark: true, delay: 0.4, count: 2, duration: 2.2, size: 4.5, strength: 0.6, dot: 2.6 },
  ],
  // a Biome or Tome it burns away: it cracks and shakes, then crumbles apart in red and orange
  'burst-incinerate': [
    { kind: 'rumble', duration: SACRIFICE_BURST, strength: 1.5 },
    { kind: 'glow', duration: 1.0, strength: 1.5, pulses: 3, size: 1.3 },
    { kind: 'cracks', duration: SACRIFICE_BURST, strength: 1.2, line: 1 },
    { kind: 'flames', duration: SACRIFICE_BURST + 0.3, count: 10, size: 2, strength: 0.9 },
    { kind: 'flash', color: 1, delay: SACRIFICE_BURST, duration: 0.3, strength: 1.3 },
    { kind: 'shards', delay: SACRIFICE_BURST, duration: 1.1, count: 22, size: 9 },
    { kind: 'shards', color: 1, delay: SACRIFICE_BURST + 0.05, duration: 1.0, count: 12, size: 7 },
    { kind: 'dust', color: 1, delay: SACRIFICE_BURST, count: 10, duration: 1.2, size: 6, strength: 0.7, dot: 3 },
    { kind: 'rise', color: 1, delay: SACRIFICE_BURST, count: 18, duration: 1.4, size: 9 },
  ],
  // a Troop Soul Collector takes lands under it
  'soul-take': [
    { kind: 'glow', duration: 0.9, strength: 1.1, size: 1 },
    { kind: 'wisp', duration: 1.2, count: 3, size: 5, strength: 0.8, dot: 1.1 },
  ],
  // Soul Collector collects: souls fly into it from all around, and it glows as it takes them in
  'souls-in': [
    { kind: 'souls', duration: 2.2, count: 8, size: 15, strength: 1, dot: 1.5 },
    { kind: 'souls', color: 1, delay: 0.3, duration: 2.0, count: 5, size: 12, strength: 0.9, dot: 1.2 },
    { kind: 'glow', delay: 1.0, duration: 1.4, strength: 1.1, pulses: 2, size: 1.15 },
    { kind: 'flash', color: 2, delay: 1.9, duration: 0.35, strength: 0.8 },
  ],
  // a Bell token rings as the opponent discards: it flashes and pulses
  'bell-ring': [
    { kind: 'flash', duration: 0.3, strength: 1.2 },
    { kind: 'shockwave', duration: 0.6, size: 9, strength: 1.2 },
    { kind: 'shockwave', color: 1, dark: true, delay: 0.25, duration: 0.6, size: 7, strength: 1 },
    { kind: 'shockwave', delay: 0.5, duration: 0.6, size: 6, strength: 0.9 },
    { kind: 'burst', count: 10, duration: 0.7, size: 4 },
  ],
  // …and the light from The River Keeper reaching one of its Bells: a small pulse there
  'bell-flash': [
    { kind: 'shockwave', color: 1, duration: 0.5, size: 6, strength: 1 },
    { kind: 'shockwave', dark: true, delay: 0.1, duration: 0.5, size: 4.5, strength: 0.9 },
  ],
  // Twilight Seedling grows at the end of its turn: a pulse
  'seedling-grow': [
    { kind: 'glow', duration: 1.1, strength: 1.2, size: 1.1 },
    { kind: 'ripple', duration: 0.7, size: 2, strength: 0.85 },
    { kind: 'twinkle', color: 1, count: 6, duration: 1, strength: 1, dot: 0.9, size: 7 },
  ],
  'aura-skulker-1': skulker(1),
  'aura-skulker-2': skulker(2),
  'aura-skulker-3': skulker(3),
  'aura-skulker-4': skulker(4),
  'aura-skulker-5': skulker(5),
  'aura-skulker-6': skulker(6),
  'aura-skulker-7': skulker(7),
  'aura-skulker-8': skulker(8),
  'aura-marlin-1': marlin(1),
  'aura-marlin-2': marlin(2),
  'aura-marlin-3': marlin(3),
  'aura-marlin-4': marlin(4),
  'aura-marlin-5': marlin(5),
  'aura-marlin-6': marlin(6),
  'aura-seedling-1': seedling(1),
  'aura-seedling-2': seedling(2),
  'aura-seedling-3': seedling(3),
  'aura-seedling-4': seedling(4),
  'aura-seedling-5': seedling(5),
  // the Twilight Beast arrives: a large purple and green burst
  'beast-born': [
    { kind: 'shake', duration: 0.6, strength: 0.7 },
    { kind: 'gather', count: 26, duration: 0.8, size: 16 },
    { kind: 'gather', color: 1, delay: 0.1, count: 18, duration: 0.75, size: 13 },
    { kind: 'flash', color: 2, delay: 0.7, duration: 0.4, strength: 1.4 },
    { kind: 'pillar', delay: 0.7, duration: 1.8, size: 26, strength: 1.2 },
    { kind: 'shockwave', delay: 0.7, duration: 0.9, size: 30, strength: 1.2 },
    { kind: 'shockwave', color: 1, delay: 0.85, duration: 0.9, size: 22, strength: 1.1 },
    { kind: 'burst', color: 1, delay: 0.7, count: 30, duration: 1.1, size: 14, strength: 1.2 },
    { kind: 'twinkle', color: 2, delay: 0.8, count: 12, duration: 1.6, strength: 1, dot: 1.2, size: 14 },
    { kind: 'rise', color: 1, delay: 0.8, count: 24, duration: 1.8, size: 14 },
    { kind: 'glow', delay: 0.6, duration: 2.2, strength: 1.3, pulses: 3, size: 1.6 },
  ],
} satisfies Record<string, Layer[]>);

// ---------------- the Ancient Spells' and Buildings' own effects ----------------
const enigma = (n: number): Layer[] => [
  { kind: 'pillar', duration: 0.6 + n * 0.25, size: 10 + n * 8, strength: 0.5 + n * 0.25 },
  { kind: 'flash', color: 1, duration: 0.3 + n * 0.08, strength: 0.4 + n * 0.25 },
  { kind: 'shockwave', delay: 0.05, duration: 0.5 + n * 0.1, size: 6 + n * 6, strength: 0.4 + n * 0.25 },
  ...(n >= 2 ? [{ kind: 'burst', color: 1, delay: 0.05, count: 6 * n, duration: 0.6, size: 3 + n * 2 } as Layer] : []),
  ...(n >= 3 ? [{ kind: 'shake', duration: 0.3, strength: 0.15 * n } as Layer, { kind: 'rise', delay: 0.1, count: 8 * n, duration: 1.2, size: 4 + n * 2 } as Layer] : []),
  ...(n >= 4 ? [{ kind: 'shockwave', color: 1, delay: 0.2, duration: 0.8, size: 34, strength: 1 } as Layer, { kind: 'glow', duration: 1.4, strength: 1.2, pulses: 2, size: 1.5 } as Layer] : []),
];
Object.assign(RECIPES, {
  // Arcane Explosion goes off: it rises, shakes harder and harder, then blows apart in a purple blast, with pink
  // rings and fragments flying
  'blast-arcane': [
    { kind: 'lift', duration: SACRIFICE_BURST, size: 5 },
    { kind: 'rumble', duration: SACRIFICE_BURST, strength: 2.2 },
    { kind: 'glow', duration: SACRIFICE_BURST, strength: 1.4, pulses: 4, size: 1.2 },
    { kind: 'gather', color: 1, duration: SACRIFICE_BURST, count: 16, size: 8 },
    { kind: 'flash', delay: SACRIFICE_BURST, duration: 0.4, strength: 1.6 },
    { kind: 'shockwave', delay: SACRIFICE_BURST, duration: 0.8, size: 34, strength: 1.4 },
    { kind: 'shockwave', color: 1, delay: SACRIFICE_BURST + 0.12, duration: 0.8, size: 24, strength: 1.2 },
    { kind: 'shockwave', color: 1, delay: SACRIFICE_BURST + 0.26, duration: 0.8, size: 16, strength: 1 },
    { kind: 'shards', delay: SACRIFICE_BURST, duration: 1.2, count: 22, size: 16 },
    { kind: 'burst', delay: SACRIFICE_BURST, count: 34, duration: 1.0, size: 15, strength: 1.4 },
    { kind: 'burst', color: 1, delay: SACRIFICE_BURST + 0.05, count: 22, duration: 1.1, size: 12, strength: 1.3 },
    { kind: 'twinkle', color: 2, delay: SACRIFICE_BURST, count: 12, duration: 1, strength: 1, dot: 1.3, size: 14 },
    { kind: 'glow', delay: SACRIFICE_BURST, duration: 1.0, strength: 1.4, size: 1.8 },
  ],
  // Bloodburst goes off: it swells and trembles, then bursts into blood (no pieces): a red spray, drops falling
  // and a dark stain spreading
  'blast-blood': [
    { kind: 'lift', duration: SACRIFICE_BURST, size: 2 },
    { kind: 'rumble', duration: SACRIFICE_BURST, strength: 1.6 },
    { kind: 'glow', duration: SACRIFICE_BURST, strength: 1.4, pulses: 3, size: 1.2 },
    { kind: 'seep', duration: SACRIFICE_BURST + 0.6, count: 4, size: 4, dot: 2, strength: 1 },
    { kind: 'flash', color: 1, delay: SACRIFICE_BURST, duration: 0.3, strength: 1.2 },
    { kind: 'burst', delay: SACRIFICE_BURST, count: 46, duration: 1.1, size: 14, strength: 1.4 },
    { kind: 'burst', color: 1, delay: SACRIFICE_BURST, count: 26, duration: 0.9, size: 10, strength: 1.3 },
    { kind: 'puff', color: 2, dark: true, delay: SACRIFICE_BURST, count: 7, duration: 1.6, size: 4, strength: 1, dot: 4.5 },
    { kind: 'fall', delay: SACRIFICE_BURST + 0.2, count: 18, duration: 1.2, size: 6, strength: 1, dot: 0.7 },
    { kind: 'shockwave', delay: SACRIFICE_BURST, duration: 0.6, size: 20, strength: 1 },
    { kind: 'glow', color: 2, dark: true, delay: SACRIFICE_BURST, duration: 1.8, strength: 1.6, size: 1.7 },
  ],
  // health taken from a Troop for Bloodburst: blood drawn out of it
  'blood-drain': [
    { kind: 'rise', count: 14, duration: 1.0, size: 6, strength: 1 },
    { kind: 'burst', color: 1, count: 10, duration: 0.6, size: 4 },
    { kind: 'glow', duration: 0.8, strength: 0.9, size: 1 },
  ],
  'enigma-1': enigma(1),
  'enigma-2': enigma(2),
  'enigma-3': enigma(3),
  'enigma-4': enigma(4),
  // Banishment: purple lightning strikes its caster's deck
  'banish-bolt': [
    { kind: 'strike', duration: 0.7, count: 2, size: 20, strength: 1.3 },
    { kind: 'flash', color: 1, duration: 0.3, strength: 1.2 },
    { kind: 'shockwave', duration: 0.5, size: 12, strength: 1 },
    { kind: 'burst', color: 1, count: 14, duration: 0.6, size: 5 },
  ],
  // …and the opponent's discard pile gives off purple sparks as the discarded cards land in it
  'banish-sparks': [
    { kind: 'burst', count: 24, duration: 0.9, size: 7, strength: 1.2 },
    { kind: 'rise', color: 1, count: 18, duration: 1.4, size: 8 },
    { kind: 'twinkle', count: 10, duration: 1.4, strength: 1, dot: 1.2, size: 7 },
    { kind: 'ripple', duration: 0.6, size: 1.8, strength: 0.9 },
  ],
  // Gerzite flying to a bar: fire round it (Devil's Coin), a glint (Gerzite Mine)
  'coin-gz': [
    { kind: 'fireflies', duration: 1.1, count: 10, size: 1.2, dot: 0.9, strength: 1.2 },
    { kind: 'fireflies', color: 1, duration: 1.1, count: 6, size: 0.9, dot: 0.6, strength: 1.2 },
    { kind: 'rise', count: 8, duration: 1, size: 3, strength: 1 },
  ],
  'crystal-gz': [
    { kind: 'twinkle', duration: 1.0, count: 6, dot: 1, size: 2.4, strength: 1.1 },
    { kind: 'fireflies', color: 1, duration: 1.0, count: 5, size: 1, dot: 0.7, strength: 1 },
  ],
  'mine-gz': [
    { kind: 'fireflies', duration: 1.0, count: 6, size: 1, dot: 0.8, strength: 1 },
    { kind: 'twinkle', color: 1, duration: 0.9, count: 4, dot: 0.9, size: 2, strength: 1 },
  ],
  // the fiery aura round cards Devil's Coin makes someone draw (it goes with them to the hand)
  fiery: [
    { kind: 'fireflies', duration: 1.1, count: 12, size: 3, dot: 1.1, strength: 1.2 },
    { kind: 'fireflies', color: 2, duration: 1.1, count: 8, size: 2.5, dot: 0.8, strength: 1.2 },
    { kind: 'orbit', color: 1, duration: 1.0, count: 2, speed: 1.8, strength: 1 },
  ],
  // Forbidden Resurgence: purple lightning strikes a Unit (which then bursts apart in purple)…
  'resurge-bolt': [
    { kind: 'strike', duration: 0.8, count: 2, size: 22, strength: 1.4 },
    { kind: 'flash', color: 1, duration: 0.3, strength: 1.3 },
    { kind: 'shockwave', duration: 0.5, size: 14, strength: 1 },
  ],
  'burst-resurge': burstIn([{ kind: 'rise', color: 1, delay: SACRIFICE_BURST, count: 18, duration: 1.4, size: 10 }]),
  // …and the card taken from the discard pile flies to the hand in a purple and black swirl
  resurge: [
    { kind: 'twinkle', duration: 1.1, count: 12, dot: 1.6, size: 5, strength: 1.2 },
    { kind: 'orbit', color: 1, duration: 1.0, count: 2, speed: 1.8, strength: 1 },
    { kind: 'fireflies', color: 2, dark: true, duration: 1.1, count: 8, size: 3, dot: 1.2, strength: 1 },
  ],
  // Infestation's Sand Crawlers come up out of the sand
  // Undead Legion: a fallen Undead Soldier breaks apart like a destroyed card, in blue (its Spirit is about to rise)
  'spirit-shatter': [
    { kind: 'flash', duration: 0.18, strength: 0.9 },
    { kind: 'shake', duration: 0.18, strength: 0.25 },
    { kind: 'cracks', duration: SACRIFICE_BURST, strength: 1.1, line: 1 },
    { kind: 'glow', delay: 0.1, duration: SACRIFICE_BURST - 0.1, strength: 0.8, pulses: 2, size: 1.05 },
    { kind: 'flash', color: 2, delay: SACRIFICE_BURST, duration: 0.3, strength: 1.2 },
    { kind: 'shards', color: 1, delay: SACRIFICE_BURST, duration: 1.2, count: 26, size: 8 },
    { kind: 'burst', delay: SACRIFICE_BURST, count: 18, duration: 0.8, size: 7, strength: 1.1 },
    { kind: 'ripple', color: 1, delay: SACRIFICE_BURST, duration: 0.7, size: 2.2, strength: 0.8 },
  ],
  // …its Spirit Soldier rising out of the shards: a soft pale-blue glow, motes drifting up with it, a ring as it settles
  'spirit-rise': [
    { kind: 'glow', duration: 2.4, strength: 1.1, pulses: 2, size: 1.2 },
    { kind: 'rise', color: 1, count: 22, duration: 2.2, size: 7 },
    { kind: 'twinkle', color: 2, duration: 2.2, count: 8, dot: 0.8, size: 6, strength: 0.9 },
    { kind: 'ripple', delay: 2.2, duration: 0.7, size: 2.0, strength: 0.7 },
  ],
  // a Spirit Soldier fading away as its General falls: a light blue glow round it as it shrinks and drifts off
  'spirit-away': [
    { kind: 'glow', duration: 1.8, strength: 1.3, size: 1.25 },
    { kind: 'glow', color: 1, duration: 1.6, strength: 0.8, size: 1.05 },
    { kind: 'twinkle', color: 1, duration: 1.8, count: 10, dot: 0.7, size: 6, strength: 1 },
    { kind: 'rise', count: 16, duration: 1.8, size: 6 },
  ],
  // Undead Legion's General arrives: it plants a battle banner in its side's color (blue or red) beside it, which waves a few seconds and fades
  'legion-banner': [
    { kind: 'banner', duration: 4.2, size: 1, strength: 1 },
    { kind: 'dust', color: 2, count: 10, duration: 1.2, size: 6, strength: 0.7, dot: 3.5 },
    { kind: 'ripple', duration: 0.8, size: 2.2, strength: 0.7 },
  ],
  // Glace Glass: a bright sheen wipes across the discard pile, twice, with a glint at its end
  'glace-sheen': [
    { kind: 'sheen', duration: 0.55, strength: 1 },
    { kind: 'sheen', color: 1, delay: 0.3, duration: 0.55, strength: 0.7 },
    { kind: 'glow', color: 1, delay: 0.1, duration: 0.9, strength: 0.6, size: 1.05 },
    { kind: 'twinkle', color: 1, delay: 0.55, duration: 0.5, count: 3, dot: 1.1, size: 4, strength: 1 },
  ],
  'sand-rise': [
    { kind: 'dust', count: 14, duration: 1.4, size: 8, strength: 1, dot: 4.5 },
    { kind: 'puff', color: 2, count: 4, duration: 1.4, size: 4, strength: 0.7, dot: 4 },
    { kind: 'burst', color: 1, count: 18, duration: 0.8, size: 6, strength: 0.9 },
    { kind: 'ripple', color: 1, duration: 0.8, size: 2, strength: 0.6 },
  ],
  // Into the Soul Realm goes off: a spectral swell over it…
  'soul-realm': [
    { kind: 'spiral', duration: 2.2, count: 4, size: 9, speed: 1.2, strength: 1.2, dot: 1.6 },
    { kind: 'wisp', color: 1, duration: 2.2, count: 4, size: 8, strength: 0.9, dot: 1.4 },
    { kind: 'glow', duration: 2, strength: 1, pulses: 2, size: 1.3 },
    { kind: 'ripple', color: 2, duration: 0.8, size: 2.2, strength: 0.9 },
  ],
  // …and the card drawn for it flies to the hand in pale wisps
  soul: [
    { kind: 'fireflies', duration: 1.1, count: 10, size: 3, dot: 1.1, strength: 1.1 },
    { kind: 'twinkle', color: 1, duration: 1.0, count: 8, dot: 1.2, size: 5, strength: 1 },
  ],
  // Persistent Shield adds Overshield: a pulse on the Emperor
  'shield-pulse': [
    { kind: 'glow', duration: 1.0, strength: 1.2, size: 1.15 },
    { kind: 'ripple', duration: 0.7, size: 2, strength: 0.9 },
    { kind: 'ripple', color: 1, delay: 0.15, duration: 0.6, size: 1.6, strength: 0.7 },
  ],
  // Spatial Rift: a black hole hangs over each of its lanes, purple and blue clouds wheeling round it
  // Lane Spells that keep working on their lanes: a scene over each lane while they are in play
  // Wingwrap: vines wrapped round each Troop it holds to the ground (they stay while the Spell does)
  'aura-vines': [{ kind: 'vines', duration: 4, count: 6, dot: 1.0, strength: 0.95 }],
  // Tangleweed's Lock: the same vines in a dark seaweed green, so the two can't be mixed up
  'aura-vines-tangle': [{ kind: 'vines', duration: 4, count: 6, dot: 1.0, strength: 0.95 }],
  // Overtime takes its toll: a burst of embers on each Emperor as a turn ends (fiercer in Double Overtime)
  'overtime-hit': [
    { kind: 'flash', duration: 0.35, strength: 0.9 },
    { kind: 'flames', color: 1, duration: 1.1, count: 8, strength: 0.8 },
    { kind: 'rise', duration: 1.2, count: 12, size: 7, strength: 1 },
  ],
  'overtime-hit-2': [
    { kind: 'flash', duration: 0.4, strength: 1.2 },
    { kind: 'shockwave', duration: 0.5, size: 12, strength: 0.9 },
    { kind: 'flames', color: 1, duration: 1.4, count: 14, strength: 1.1 },
    { kind: 'rise', duration: 1.4, count: 20, size: 9, strength: 1.2 },
  ],
  'lane-toxic': [
    { kind: 'rimpuff', dark: true, duration: 4, count: 14, dot: 4.2, strength: 1 },
    { kind: 'rimpuff', color: 1, duration: 4, count: 6, dot: 2.6, strength: 0.35 },
  ],
  'lane-blizzard': [
    { kind: 'drift', duration: 2.6, count: 6, strength: 0.8 },
    { kind: 'fall', color: 1, duration: 2.6, count: 18, size: 7, dot: 0.55, strength: 1 },
    { kind: 'drift', color: 1, delay: 1.2, duration: 2.2, count: 4, strength: 0.6 },
  ],
  'lane-slag': [
    { kind: 'rocks', duration: 4, count: 6, dot: 1.4, strength: 0.9 },
    { kind: 'flames', color: 1, duration: 4, count: 9, strength: 0.75 },
  ],
  'lane-quake': [
    { kind: 'cracks', hold: true, duration: 4, scale: 0.55, strength: 0.6, line: 1 },
    { kind: 'rocks', color: 0, duration: 4, count: 5, dot: 0.9, strength: 0.45 },
  ],
  'lane-scald': [
    { kind: 'halo', duration: 3, strength: 0.95, pulses: 2 },
    { kind: 'halo', color: 1, dark: true, duration: 3, size: 1.04, strength: 0.9 },
  ],
  'lane-totem': [
    { kind: 'halo', duration: 3, strength: 0.9, pulses: 2 },
    { kind: 'halo', color: 1, duration: 3, size: 1.06, strength: 0.6 },
    { kind: 'twinkle', color: 1, duration: 2.8, count: 4, dot: 0.7, size: 6, strength: 0.6 },
  ],
  'lane-surge': [
    { kind: 'halo', duration: 3, strength: 0.95, pulses: 2 },
    { kind: 'streak', delay: 0.6, duration: 0.42, count: 3, size: 1.6 },
    { kind: 'streak', color: 1, delay: 0.72, duration: 0.36, count: 2, size: 1.3 },
    { kind: 'ripple', color: 1, delay: 0.7, duration: 0.4, size: 1.9, strength: 0.9 },
  ],
  'lane-petals': [
    { kind: 'fall', duration: 3.2, count: 14, size: 6, dot: 1.1, strength: 0.95 },
    { kind: 'glow', color: 1, duration: 3.2, strength: 0.35, size: 0.95 },
    { kind: 'halo', color: 1, duration: 3.2, strength: 0.45 },
  ],
  // Royal Delivery: a little smoke rising from the lane the Royal Guard will come to, in its caster's color
  'lane-smoke': [
    { kind: 'puff', duration: 2.6, count: 5, size: 7, dot: 3.4, strength: 0.95 },
    { kind: 'rise', duration: 2.4, count: 6, size: 6, strength: 0.8 },
  ],
  // Timed Blast: a soft glow pulsing in the lane it will blow up, in its caster's color
  'lane-glow': [
    { kind: 'glow', duration: 2.6, strength: 0.75, size: 0.95, pulses: 2 },
    { kind: 'twinkle', color: 1, duration: 2.4, count: 4, dot: 0.8, size: 2, strength: 0.7 },
  ],
  // Vacuum: a pocket of empty space over the lane it holds: a dark void turning, distant stars twinkling in it and
  // specks of light drawn in and swallowed
  'lane-vacuum': [
    { kind: 'halo', dark: true, duration: 3.6, strength: 0.9 },
    { kind: 'vortex', duration: 3.6, count: 10, size: 5, dot: 2.2, strength: 0.75 },
    { kind: 'twinkle', color: 2, duration: 3.4, count: 10, dot: 0.7, size: 7, strength: 0.9 },
    { kind: 'gather', color: 1, delay: 0.5, duration: 1.6, count: 12, size: 8, strength: 0.75 },
    { kind: 'gather', color: 2, delay: 2.0, duration: 1.4, count: 8, size: 7, strength: 0.6 },
  ],
  // The River Keeper's Bell: the top card of the deck is knocked off it, in a pale green flash
  'bell-mill': [
    { kind: 'glow', color: 1, duration: 0.7, strength: 1.1, size: 1.1 },
    { kind: 'ripple', color: 1, duration: 0.5, size: 1.6, strength: 0.8 },
  ],
  // Grave Intercessor's ward over the Troop it chose: a pale ring of light and souls slowly circling above it
  'intercede-ward': [
    { kind: 'halo', duration: 3.2, strength: 0.7, pulses: 2 },
    { kind: 'orbit', color: 1, duration: 3.2, count: 2, speed: 0.45, strength: 0.6 },
    { kind: 'wisp', color: 1, delay: 0.4, duration: 2.6, count: 3, strength: 0.55 },
  ],
  // Cagurn's Thorny Snare: thorny vines curled over the lane it lies in, and sand blowing across it
  'lane-snare': [
    { kind: 'vines', duration: 4, count: 7, dot: 1.1, strength: 0.95 },
    { kind: 'drift', color: 1, duration: 3.4, count: 4, strength: 0.55 },
  ],
  // …and when it springs on a Unit: thorns snap shut on it
  'snare-bite': [
    { kind: 'flash', color: 1, duration: 0.2, strength: 0.6 },
    { kind: 'shards', duration: 0.6, count: 10, size: 5, strength: 0.9 },
  ],
  'rift-hole': [
    { kind: 'vortex', duration: 4, count: 14, size: 6, dot: 3, strength: 1 },
    { kind: 'gather', color: 1, delay: 0.5, duration: 1.4, count: 10, size: 9, strength: 0.8 },
    { kind: 'gather', color: 2, delay: 2.2, duration: 1.4, count: 8, size: 9, strength: 0.8 },
  ],
  // Tectonic Shift: the ground gives way in each lane (cracks, a dark pit, dust, debris drawn down into it)…
  'tectonic-sink': [
    { kind: 'shake', duration: 0.7, strength: 0.8 },
    { kind: 'cracks', duration: 0.8, strength: 1.3, line: 0 },
    { kind: 'glow', color: 0, dark: true, duration: 2.2, strength: 1.8, size: 1.3 },
    { kind: 'dust', color: 2, count: 16, duration: 1.8, size: 10, strength: 1, dot: 5 },
    { kind: 'gather', color: 0, dark: true, delay: 0.2, duration: 1.2, count: 20, size: 9, strength: 1 },
    { kind: 'puff', color: 2, delay: 0.3, count: 5, duration: 2, size: 5, strength: 0.7, dot: 5 },
    { kind: 'rise', color: 1, count: 12, duration: 1.2, size: 5 },
  ],
  // …and the cracks stay while it is in play, glowing faintly
  'tectonic-crack': [
    { kind: 'cracks', hold: true, duration: 4, strength: 0.7, line: 1 },
    { kind: 'pit', duration: 4, size: 0.55, strength: 0.7 },
    { kind: 'rise', count: 3, duration: 3, size: 3, strength: 0.6 },
  ],
  // Voodoo Magic: each of the two Units it links flares purple as the beam reaches it
  'voodoo-beam': [
    { kind: 'glow', duration: 1.1, strength: 1.2, size: 1.1 },
    { kind: 'ripple', duration: 0.6, size: 1.8, strength: 0.8 },
    { kind: 'twinkle', color: 1, count: 5, duration: 1, strength: 0.9, dot: 0.9, size: 6 },
  ],
  // a Shield counter breaking: it flashes and shatters into blue shards that scatter and fall, with a ring of light
  'shield-shatter': [
    { kind: 'flash', duration: 0.25, strength: 1.1 },
    { kind: 'shards', duration: 1.0, count: 16, size: 6, dot: 0.25, strength: 1 },
    { kind: 'shards', color: 1, duration: 0.9, count: 8, size: 5, dot: 0.2, strength: 1 },
    { kind: 'burst', color: 1, count: 14, duration: 0.6, size: 4, strength: 1.2 },
    { kind: 'shockwave', duration: 0.5, size: 8, strength: 1 },
  ],
  // Templar Beam attacks: red and purple sparks are drawn into it, and when the attack is done it lets out a dark red pulse
  'templar-charge': [
    { kind: 'gather', duration: 0.6, count: 22, size: 10 },
    { kind: 'gather', color: 1, delay: 0.05, duration: 0.55, count: 16, size: 9 },
    { kind: 'glow', duration: 0.7, strength: 1.2, size: 1.1 },
    { kind: 'glow', color: 2, dark: true, delay: 1.1, duration: 0.9, strength: 1.8, size: 1.4 },
    { kind: 'shockwave', color: 2, dark: true, delay: 1.1, duration: 0.8, size: 18, strength: 1.4 },
    { kind: 'shockwave', delay: 1.15, duration: 0.7, size: 12, strength: 0.9 },
  ],
} satisfies Record<string, Layer[]>);

// ---------------- shared textures ----------------
const CW = CONFIG.card.width;
const CH = CONFIG.card.height;

function canvasTexture(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d')!);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

let textures: {
  ring: THREE.Texture; soft: THREE.Texture; circle: THREE.Texture; dot: THREE.Texture; column: THREE.Texture; cracks: THREE.Texture; cloud: THREE.Texture; spark: THREE.Texture; frame: THREE.Texture;
  drip: THREE.Texture; trail: THREE.Texture; riser: THREE.Texture; sheen: THREE.Texture; bolts: THREE.Texture[]; bird: THREE.Texture; flame: THREE.Texture;
} | null = null;
export function fxTextures() {
  return tex();
}
function tex() {
  if (textures) return textures;
  const ring = canvasTexture(256, 340, (ctx) => {
    ctx.strokeStyle = '#fff';
    ctx.shadowColor = '#fff';
    for (const [blur, width, alpha] of [[24, 10, 0.5], [10, 6, 0.9], [0, 3, 1]] as const) {
      ctx.globalAlpha = alpha;
      ctx.shadowBlur = blur;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.roundRect(30, 30, 196, 280, 22);
      ctx.stroke();
    }
  });
  const soft = canvasTexture(256, 340, (ctx) => {
    for (let i = 0; i < 22; i++) {
      const inset = i * 5;
      ctx.fillStyle = `rgba(255,255,255,${0.035 + i * 0.004})`;
      ctx.beginPath();
      ctx.roundRect(inset, inset, 256 - inset * 2, 340 - inset * 2, 60 - i * 2);
      ctx.fill();
    }
  });
  const circle = canvasTexture(256, 256, (ctx) => {
    ctx.strokeStyle = '#fff';
    ctx.shadowColor = '#fff';
    for (const [blur, width, alpha] of [[30, 14, 0.5], [12, 8, 0.85], [0, 3, 1]] as const) {
      ctx.globalAlpha = alpha;
      ctx.shadowBlur = blur;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.arc(128, 128, 96, 0, Math.PI * 2);
      ctx.stroke();
    }
  });
  const dot = canvasTexture(64, 64, (ctx) => {
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.65)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
  });
  const column = canvasTexture(128, 256, (ctx) => {
    // bright at the base, fading out toward the top and the sides
    const up = ctx.createLinearGradient(0, 256, 0, 0);
    up.addColorStop(0, 'rgba(255,255,255,0.95)');
    up.addColorStop(0.35, 'rgba(255,255,255,0.5)');
    up.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = up;
    ctx.fillRect(0, 0, 128, 256);
    ctx.globalCompositeOperation = 'destination-in';
    const sides = ctx.createLinearGradient(0, 0, 128, 0);
    sides.addColorStop(0, 'rgba(255,255,255,0)');
    sides.addColorStop(0.5, 'rgba(255,255,255,1)');
    sides.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sides;
    ctx.fillRect(0, 0, 128, 256);
  });
  // jagged cracks running out from a point near the middle of a card, each with a couple of side branches
  const cracks = canvasTexture(256, 358, (ctx) => {
    let seed = 7;
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    const cx = 118;
    const cy = 170;
    ctx.strokeStyle = '#fff';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const crack = (x: number, y: number, angle: number, length: number, width: number, depth: number) => {
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(x, y);
      const steps = Math.max(3, Math.round(length / 16));
      for (let i = 0; i < steps; i++) {
        angle += (rnd() - 0.5) * 0.9;
        x += Math.cos(angle) * (length / steps);
        y += Math.sin(angle) * (length / steps);
        ctx.lineTo(x, y);
        if (depth > 0 && rnd() < 0.35) {
          ctx.stroke();
          crack(x, y, angle + (rnd() < 0.5 ? 1 : -1) * (0.6 + rnd() * 0.6), length * 0.4, width * 0.6, depth - 1);
          ctx.lineWidth = width;
          ctx.beginPath();
          ctx.moveTo(x, y);
        }
      }
      ctx.stroke();
    };
    for (let i = 0; i < 9; i++) crack(cx, cy, (i / 9) * Math.PI * 2 + rnd() * 0.5, 120 + rnd() * 90, 4.2, 2);
  });
  // a loose, soft blob: smoke, mist, a cloud of sand
  const cloud = canvasTexture(128, 128, (ctx) => {
    for (const [x, y, r, a] of [[64, 66, 58, 0.5], [46, 54, 34, 0.45], [84, 60, 32, 0.45], [62, 84, 30, 0.4]] as const) {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(255,255,255,${a})`);
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 128, 128);
    }
  });
  // a four-pointed glint
  const spark = canvasTexture(64, 64, (ctx) => {
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 14);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = '#fff';
    for (const turn of [0, Math.PI / 2]) {
      ctx.save();
      ctx.translate(32, 32);
      ctx.rotate(turn);
      ctx.beginPath();
      ctx.moveTo(-30, 0);
      ctx.quadraticCurveTo(0, -3.2, 30, 0);
      ctx.quadraticCurveTo(0, 3.2, -30, 0);
      ctx.fill();
      ctx.restore();
    }
  });
  // one floor of a building going up: a crisp outline the shape of a card, with a cross-brace
  const frame = canvasTexture(250, 350, (ctx) => {
    ctx.strokeStyle = '#fff';
    ctx.shadowColor = '#fff';
    for (const [blur, width, alpha] of [[14, 7, 0.55], [0, 5, 1]] as const) {
      ctx.globalAlpha = alpha;
      ctx.shadowBlur = blur;
      ctx.lineWidth = width;
      ctx.strokeRect(12, 12, 226, 326);
    }
    ctx.globalAlpha = 0.5;
    ctx.shadowBlur = 0;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(12, 12);
    ctx.lineTo(238, 338);
    ctx.moveTo(238, 12);
    ctx.lineTo(12, 338);
    ctx.stroke();
  });
  // a thin trail of blood running down a card: soft at the sides, a little wider toward the drop at its foot
  const drip = canvasTexture(32, 128, (ctx) => {
    for (let y = 0; y < 128; y++) {
      const half = 5 + (y / 127) * 4;
      const g = ctx.createLinearGradient(16 - half, 0, 16 + half, 0);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(0.5, 'rgba(255,255,255,1)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(16 - half, y, half * 2, 1);
    }
  });
  // a speed streak lying along +x: nothing at its tail, brightest just behind its rounded head
  const trail = canvasTexture(256, 32, (ctx) => {
    const along = ctx.createLinearGradient(0, 0, 256, 0);
    along.addColorStop(0, 'rgba(255,255,255,0)');
    along.addColorStop(0.8, 'rgba(255,255,255,0.85)');
    along.addColorStop(0.94, 'rgba(255,255,255,1)');
    along.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = along;
    ctx.fillRect(0, 0, 256, 32);
    ctx.globalCompositeOperation = 'destination-in';
    const across = ctx.createLinearGradient(0, 0, 0, 32);
    across.addColorStop(0, 'rgba(255,255,255,0)');
    across.addColorStop(0.5, 'rgba(255,255,255,1)');
    across.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = across;
    ctx.fillRect(0, 0, 256, 32);
  });
  // a rising streak standing upright: a bright round head at the top and a tail fading away below it
  const riser = canvasTexture(32, 128, (ctx) => {
    const down = ctx.createLinearGradient(0, 0, 0, 128);
    down.addColorStop(0, 'rgba(255,255,255,0)');
    down.addColorStop(0.08, 'rgba(255,255,255,1)');
    down.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = down;
    ctx.fillRect(0, 0, 32, 128);
    ctx.globalCompositeOperation = 'destination-in';
    const sides = ctx.createLinearGradient(0, 0, 32, 0);
    sides.addColorStop(0, 'rgba(255,255,255,0)');
    sides.addColorStop(0.5, 'rgba(255,255,255,1)');
    sides.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sides;
    ctx.fillRect(0, 0, 32, 128);
  });
  // a slanted band of light, for a gleam sweeping across a card's face. It stays well inside the picture's
  // edges, so a copy slid sideways (texture offset) leaves nothing behind at the card's border.
  const sheen = canvasTexture(256, 358, (ctx) => {
    ctx.translate(128, 179);
    ctx.rotate(0.35);
    for (const [half, alpha] of [[30, 0.45], [11, 1]] as const) {
      const g = ctx.createLinearGradient(-half, 0, half, 0);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(0.5, `rgba(255,255,255,${alpha})`);
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(-half, -260, half * 2, 520);
    }
  });
  // forks of lightning running left to right across the picture (their foot at the left middle), with a glow
  // round them and a small branch or two. Four, so the strikes don't all look alike.
  let seed = 11;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const bolts = [0, 1, 2, 3].map(() =>
    canvasTexture(256, 96, (ctx) => {
      const jag = (x0: number, y0: number, x1: number, y1: number, rough: number): [number, number][] => {
        let pts: [number, number][] = [[x0, y0], [x1, y1]];
        for (let level = 0; level < 5; level++) {
          const next: [number, number][] = [pts[0]];
          for (let i = 1; i < pts.length; i++) {
            const [ax, ay] = pts[i - 1];
            const [bx, by] = pts[i];
            next.push([(ax + bx) / 2 + (rnd() - 0.5) * rough * 0.3, (ay + by) / 2 + (rnd() - 0.5) * rough], [bx, by]);
          }
          pts = next;
          rough *= 0.55;
        }
        return pts;
      };
      const main = jag(4, 48, 248, 48 + (rnd() - 0.5) * 34, 46);
      const forks = [0.35, 0.6].map((at) => {
        const [fx, fy] = main[Math.floor(main.length * (at + rnd() * 0.15))];
        return jag(fx, fy, fx + 50 + rnd() * 50, fy + (rnd() < 0.5 ? -1 : 1) * (14 + rnd() * 16), 22);
      });
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#fff';
      ctx.shadowColor = '#fff';
      for (const [blur, width, alpha] of [[14, 6, 0.4], [6, 3, 0.85], [0, 1.6, 1]] as const) {
        ctx.globalAlpha = alpha;
        ctx.shadowBlur = blur;
        for (const [line, thin] of [[main, 1], ...forks.map((f) => [f, 0.6] as const)] as const) {
          ctx.lineWidth = width * thin;
          ctx.beginPath();
          line.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
          ctx.stroke();
        }
      }
    }),
  );
  // a bird seen from below, wings spread: two curved strokes meeting at the body (for a flock circling a card)
  const bird = canvasTexture(128, 64, (ctx) => {
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.moveTo(4, 30);
    ctx.quadraticCurveTo(34, 8, 64, 34);
    ctx.quadraticCurveTo(94, 8, 124, 30);
    ctx.quadraticCurveTo(94, 20, 64, 46);
    ctx.quadraticCurveTo(34, 20, 4, 30);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(64, 40, 5, 9, 0, 0, Math.PI * 2);
    ctx.fill();
  });
  // a tongue of flame: a soft teardrop, widest and brightest near its foot, drawn to a point at the top
  const flame = canvasTexture(64, 128, (ctx) => {
    for (const [scale, alpha] of [[1, 0.35], [0.7, 0.6], [0.42, 1]] as const) {
      ctx.globalAlpha = alpha;
      const g = ctx.createRadialGradient(32, 100, 0, 32, 100, 30 * scale);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(32, 128 - 128 * scale);
      ctx.quadraticCurveTo(32 + 30 * scale, 80, 32 + 22 * scale, 110);
      ctx.quadraticCurveTo(32, 128, 32 - 22 * scale, 110);
      ctx.quadraticCurveTo(32 - 30 * scale, 80, 32, 128 - 128 * scale);
      ctx.fill();
    }
  });
  textures = { ring, soft, circle, dot, column, cracks, cloud, spark, frame, drip, trail, riser, sheen, bolts, bird, flame };
  return textures;
}

function flatPlane(w: number, h: number) {
  const g = new THREE.PlaneGeometry(w, h);
  g.rotateX(-Math.PI / 2);
  return g;
}

const ease = (t: number) => 1 - Math.pow(1 - t, 3);

// ---------------- running effects ----------------
interface Part {
  layer: Layer;
  start: number;
  update(t: number, time: number): void;
  objects: THREE.Object3D[];
  started: boolean;
}

interface RunningEffect {
  kind: string;
  group: THREE.Group;
  follow: THREE.Object3D | null;
  parts: Part[];
  born: number;
  length: number;
}

export class Effects {
  private running: RunningEffect[] = [];
  private scene: THREE.Scene;
  private onShake: (strength: number, duration: number) => void;

  constructor(scene: THREE.Scene, onShake: (strength: number, duration: number) => void) {
    this.scene = scene;
    this.onShake = onShake;
  }

  /** How long an effect runs, in seconds (0 if it wouldn't play at all). */
  lengthOf(kind: string): number {
    const recipe = RECIPES[kind];
    if (!CONFIG.playEffects.enabled || !recipe || !CONFIG.playEffects.colors[kind]?.length) return 0;
    return Math.max(...recipe.map((l) => (l.delay ?? 0) + l.duration));
  }

  /** Plays the effect for a rarity (or 'emperor') around a card. */
  play(kind: string, follow: THREE.Object3D, tint?: string[]) {
    if (!CONFIG.playEffects.enabled) return;
    const recipe = RECIPES[kind];
    const colors = tint?.length ? tint : CONFIG.playEffects.colors[kind]; // (tint: colors of its own this time — a lane marked in its caster's color)
    if (!recipe || !colors?.length) return;
    const group = new THREE.Group();
    group.position.copy(follow.position);
    if (typeof follow.userData.fxY === 'number') group.position.y = follow.userData.fxY;
    group.rotation.y = follow.rotation.y;
    group.scale.setScalar(this.sizeOf(follow)); // (a card shrunk to share its lane: its effects are shrunk with it)
    this.scene.add(group);
    const now = performance.now() / 1000;
    const parts = recipe.map((layer) => this.makePart(layer, colors, group, now, follow));
    const length = Math.max(...recipe.map((l) => (l.delay ?? 0) + l.duration));
    this.running.push({ kind, group, follow, parts, born: now, length });
  }

  private makePart(layer: Layer, colors: string[], group: THREE.Group, now: number, follow: THREE.Object3D): Part {
    const color = new THREE.Color(colors[Math.min(layer.color ?? 0, colors.length - 1)]);
    const t = tex();
    const strength = layer.strength ?? 1;
    const material = (map: THREE.Texture) =>
      new THREE.MeshBasicMaterial({
        map,
        color: layer.dark ? new THREE.Color(color).multiplyScalar(0.35) : color,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: layer.dark ? THREE.NormalBlending : THREE.AdditiveBlending,
      });
    const part: Part = { layer, start: now + (layer.delay ?? 0), objects: [], started: false, update: () => {} };
    const add = (o: THREE.Object3D) => {
      o.visible = false;
      group.add(o);
      part.objects.push(o);
      return o;
    };

    switch (layer.kind) {
      case 'ripple': {
        const m = add(new THREE.Mesh(flatPlane(CW * 1.3, CH * 1.3), material(t.ring))) as THREE.Mesh;
        m.position.y = 0.25;
        const size = layer.size ?? 2;
        part.update = (k) => {
          const s = 1 + (size - 1) * ease(k);
          m.scale.set(s, 1, s);
          (m.material as THREE.MeshBasicMaterial).opacity = strength * Math.pow(1 - k, 1.5);
        };
        break;
      }
      case 'glow': {
        const size = layer.size ?? 1;
        const m = add(new THREE.Mesh(flatPlane(CW * 2.1 * size, CH * 1.85 * size), material(t.soft))) as THREE.Mesh;
        m.position.y = layer.dark ? -0.02 : -0.01;
        const pulses = layer.pulses ?? 1;
        part.update = (k) => {
          const env = Math.pow(Math.sin(Math.PI * k), 0.6);
          const beat = 1 - 0.35 * (0.5 - 0.5 * Math.cos(Math.PI * 2 * pulses * k));
          (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * 0.75 * env * beat);
          const s = 1 + 0.08 * Math.sin(Math.PI * 2 * pulses * k);
          m.scale.set(s, 1, s);
        };
        break;
      }
      case 'flash': {
        const m = add(new THREE.Mesh(flatPlane(CW * 1.5, CH * 1.35), material(t.soft))) as THREE.Mesh;
        m.position.y = 0.3;
        part.update = (k) => {
          (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * Math.pow(1 - k, 3));
        };
        break;
      }
      case 'shockwave': {
        const m = add(new THREE.Mesh(flatPlane(2, 2), material(t.circle))) as THREE.Mesh;
        m.position.y = 0.2;
        const size = layer.size ?? 25;
        part.update = (k) => {
          const s = 3 + (size - 3) * ease(k);
          m.scale.set(s, 1, s);
          (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * Math.pow(1 - k, 2));
        };
        break;
      }
      case 'orbit': {
        const trails = layer.count ?? 2;
        const perTrail = 9;
        const dots: THREE.Mesh[] = [];
        for (let i = 0; i < trails * perTrail; i++) {
          const d = add(new THREE.Mesh(flatPlane(1.6, 1.6), material(t.dot))) as THREE.Mesh;
          d.position.y = 0.35;
          dots.push(d);
        }
        const rx = CW / 2 + 1.3;
        const rz = CH / 2 + 1.3;
        const speed = layer.speed ?? 1.5;
        const n = 4; // rounded-rectangle path
        const at = (a: number) => {
          const c = Math.cos(a);
          const s = Math.sin(a);
          return [rx * Math.sign(c) * Math.pow(Math.abs(c), 2 / n), rz * Math.sign(s) * Math.pow(Math.abs(s), 2 / n)];
        };
        part.update = (k) => {
          const env = Math.pow(Math.sin(Math.PI * k), 0.5);
          const lead = k * layer.duration * speed * Math.PI * 2;
          dots.forEach((d, i) => {
            const trail = Math.floor(i / perTrail);
            const j = i % perTrail;
            const a = lead + (trail * Math.PI * 2) / trails - j * 0.12;
            const [x, z] = at(a);
            d.position.x = x;
            d.position.z = z;
            const fade = 1 - j / perTrail;
            const s = 0.4 + 0.8 * fade;
            d.scale.set(s, 1, s);
            (d.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * env * fade);
          });
        };
        break;
      }
      case 'burst': {
        const count = layer.count ?? 14;
        const size = layer.size ?? 8;
        const sparks: { m: THREE.Mesh; dx: number; dz: number; dist: number; lift: number }[] = [];
        for (let i = 0; i < count; i++) {
          const m = add(new THREE.Mesh(flatPlane(1.1, 1.1), material(t.dot))) as THREE.Mesh;
          const a = Math.random() * Math.PI * 2;
          sparks.push({ m, dx: Math.cos(a), dz: Math.sin(a), dist: size * (0.5 + Math.random() * 0.6), lift: 1 + Math.random() * 3 });
        }
        part.update = (k) => {
          const e = ease(k);
          for (const s of sparks) {
            s.m.position.set(s.dx * s.dist * e, 0.4 + Math.sin(Math.PI * k) * s.lift, s.dz * s.dist * e);
            const sc = 1.2 - 0.8 * k;
            s.m.scale.set(sc, 1, sc);
            (s.m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * (1 - k));
          }
        };
        break;
      }
      case 'rise': {
        // sparks lift off the card's edges and float upward
        const count = layer.count ?? 16;
        const height = layer.size ?? 8;
        const sparks: { sp: THREE.Sprite; x: number; z: number; start: number; drift: number; h: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(
              new THREE.SpriteMaterial({ map: t.dot, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }),
            ),
          ) as THREE.Sprite;
          // a random spot on the card's outline (some just inside it)
          const side = Math.floor(Math.random() * 4);
          const along = Math.random() * 2 - 1;
          const inset = 0.8 + Math.random() * 0.25;
          const x = (side < 2 ? along : side === 2 ? -1 : 1) * (CW / 2) * inset;
          const z = (side < 2 ? (side === 0 ? -1 : 1) : along) * (CH / 2) * inset;
          sparks.push({ sp, x, z, start: Math.random() * 0.45, drift: (Math.random() - 0.5) * 1.6, h: height * (0.6 + Math.random() * 0.5) });
        }
        part.update = (k) => {
          for (const sk of sparks) {
            const local = Math.min(1, Math.max(0, (k - sk.start) / (1 - sk.start)));
            sk.sp.position.set(sk.x + sk.drift * local, 0.3 + sk.h * ease(local), sk.z);
            const size = 0.9 * (1 - 0.6 * local);
            sk.sp.scale.set(size, size * (1 + 1.6 * (1 - local)), 1); // stretched while moving fast
            sk.sp.material.opacity = local <= 0 ? 0 : Math.min(1, strength * Math.sin(Math.PI * Math.min(1, local * 1.15)));
          }
        };
        break;
      }
      case 'gather': {
        // sparks appear in a wide ring around the card, curve inward and sink into it
        // (dark: they are painted over the table rather than added to it as light, so black sparks show)
        const count = layer.count ?? 20;
        const reach = layer.size ?? 12;
        const sparks: { sp: THREE.Sprite; a: number; r: number; start: number; swirl: number; lift: number; tx: number; tz: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(
              new THREE.SpriteMaterial({ map: t.dot, color, transparent: true, opacity: 0, depthWrite: false, blending: layer.dark ? THREE.NormalBlending : THREE.AdditiveBlending }),
            ),
          ) as THREE.Sprite;
          sparks.push({
            sp,
            a: (i / count) * Math.PI * 2 + Math.random() * 0.5,
            r: reach * (0.75 + Math.random() * 0.5),
            start: Math.random() * 0.4, // they don't all set off at once
            swirl: (0.5 + Math.random() * 0.6) * (i % 2 ? 1 : -1),
            lift: 1.5 + Math.random() * 4,
            tx: (Math.random() - 0.5) * CW * 0.5, // where on the card it lands
            tz: (Math.random() - 0.5) * CH * 0.5,
          });
        }
        part.update = (k) => {
          for (const sk of sparks) {
            const local = Math.min(1, Math.max(0, (k - sk.start) / (1 - sk.start)));
            const pull = local * local * (3 - 2 * local); // slow to leave, quick through the middle, soft landing
            const a = sk.a + sk.swirl * pull;
            const r = sk.r * (1 - pull);
            sk.sp.position.set(sk.tx * pull + Math.cos(a) * r, 0.35 + Math.sin(Math.PI * pull) * sk.lift, sk.tz * pull + Math.sin(a) * r);
            const size = 1.25 - 0.75 * pull;
            sk.sp.scale.set(size, size, 1);
            // quick to appear, gone the moment it touches the card
            sk.sp.material.opacity = local <= 0 || local >= 1 ? 0 : Math.min(1, strength * Math.min(1, local * 6) * Math.min(1, (1 - local) * 9));
          }
        };
        break;
      }
      case 'puff': {
        // soft clouds roll up off the card, swelling and thinning as they go: smoke (dark) or mist
        const count = layer.count ?? 3;
        const height = layer.size ?? 4;
        const big = layer.dot ?? 2.5;
        const puffs: { sp: THREE.Sprite; x: number; z: number; start: number; drift: number; turn: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(
              new THREE.SpriteMaterial({
                map: t.cloud,
                color: layer.dark ? new THREE.Color(color).multiplyScalar(0.8) : new THREE.Color(color),
                transparent: true,
                opacity: 0,
                depthWrite: false,
                // smoke, and anything that has to end up black, is painted over the table; plain mist is light added to it
                blending: layer.dark || layer.toBlack ? THREE.NormalBlending : THREE.AdditiveBlending,
              }),
            ),
          ) as THREE.Sprite;
          puffs.push({ sp, x: (Math.random() - 0.5) * CW * 0.6, z: (Math.random() - 0.5) * CH * 0.5, start: (i / count) * 0.45 + Math.random() * 0.08, drift: (Math.random() - 0.5) * 2.2, turn: Math.random() * 6.28 });
        }
        part.update = (k) => {
          for (const p of puffs) {
            const local = Math.min(1, Math.max(0, (k - p.start) / (1 - p.start)));
            p.sp.position.set(p.x + p.drift * local, 0.6 + height * ease(local), p.z);
            const s = big * (0.6 + 0.9 * local);
            p.sp.scale.set(s, s, 1);
            p.sp.material.rotation = p.turn + local * 0.6;
            p.sp.material.opacity = local <= 0 ? 0 : Math.min(1, strength * Math.sin(Math.PI * local) ** 0.8);
            if (layer.toBlack) p.sp.material.color.copy(color).lerp(BLACK, Math.min(1, local * 1.25)); // its own color at the card, black by the top
          }
        };
        break;
      }
      case 'build': {
        // a building going up: `count` floors (card-shaped frames) appear one above the other on four corner
        // posts, `size` cm tall in all. Then the whole frame flashes, sinks into the card and fades.
        const floors = Math.max(1, layer.count ?? 4);
        const height = layer.size ?? 4;
        const fit = 0.94; // just inside the card's edge
        const levels: THREE.Mesh[] = [];
        for (let i = 0; i < floors; i++) {
          const m = add(new THREE.Mesh(flatPlane(CW * fit, CH * fit), material(t.frame))) as THREE.Mesh;
          levels.push(m);
        }
        const posts: THREE.Mesh[] = [];
        for (const [sx, sz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
          const geo = new THREE.BoxGeometry(0.14, 1, 0.14);
          geo.translate(0, 0.5, 0); // it grows upward from its foot
          const post = add(new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Mesh;
          post.position.set(sx * (CW * fit) / 2 * 0.955, 0.3, sz * (CH * fit) / 2 * 0.965);
          posts.push(post);
        }
        const rise = 0.6; // the share of the time spent building; then a short hold, then it settles
        const hold = 0.76;
        part.update = (k) => {
          const built = Math.min(1, k / rise); // 0 → 1 as the floors go up
          const settle = k <= hold ? 0 : (k - hold) / (1 - hold); // 0 → 1 as it sinks into the card
          const fade = 1 - settle;
          const flash = k > rise && k <= hold ? 1 + 0.5 * Math.sin(((k - rise) / (hold - rise)) * Math.PI) : 1;
          const top = height * (1 - settle * settle);
          levels.forEach((m, i) => {
            const at = (i + 1) / floors; // when this floor is reached, and how high it sits
            const shown = Math.min(1, Math.max(0, (built - (at - 1 / floors)) * floors));
            // each floor drops the last little way into place
            m.position.y = 0.3 + top * at + (1 - shown) * 0.7;
            (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * shown * fade * flash * (0.55 + 0.45 * at));
          });
          for (const post of posts) {
            post.scale.y = Math.max(0.01, top * built);
            (post.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * 0.8 * fade * flash * Math.min(1, built * 4));
          }
        };
        break;
      }
      case 'dust': {
        // dust kicked up by something landing hard: clouds roll outward along the table from the card's
        // edges, quick at first, swelling and thinning as they slow
        const count = layer.count ?? 10;
        const reach = layer.size ?? 8;
        const big = layer.dot ?? 3;
        const clouds: { sp: THREE.Sprite; x: number; z: number; dx: number; dz: number; far: number; turn: number; lift: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.cloud, color: new THREE.Color(color), transparent: true, opacity: 0, depthWrite: false }))) as THREE.Sprite;
          const a = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
          const dx = Math.cos(a);
          const dz = Math.sin(a);
          // they start on the card's edge, in the direction they travel
          const edge = Math.min((CW / 2) / Math.max(0.001, Math.abs(dx)), (CH / 2) / Math.max(0.001, Math.abs(dz)));
          clouds.push({ sp, x: dx * edge * 0.9, z: dz * edge * 0.9, dx, dz, far: reach * (0.65 + Math.random() * 0.5), turn: Math.random() * 6.28, lift: 0.5 + Math.random() * 0.9 });
        }
        part.update = (k) => {
          const e = ease(k);
          for (const c of clouds) {
            c.sp.position.set(c.x + c.dx * c.far * e, 0.5 + c.lift * e, c.z + c.dz * c.far * e);
            const sc = big * (0.45 + 0.9 * e);
            c.sp.scale.set(sc, sc, 1);
            c.sp.material.rotation = c.turn + k * 0.8;
            c.sp.material.opacity = Math.min(1, strength * Math.min(1, k * 10) * (1 - k) ** 1.3);
          }
        };
        break;
      }
      case 'drift': {
        // streaks of dust sweep across the card on the wind, always the same way across the table
        const count = layer.count ?? 5;
        const span = layer.size ?? 9;
        const long = layer.dot ?? 3;
        const gusts: { sp: THREE.Sprite; across: number; y: number; start: number; wave: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(new THREE.SpriteMaterial({ map: t.cloud, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending })),
          ) as THREE.Sprite;
          gusts.push({ sp, across: (Math.random() - 0.5) * 6, y: 0.7 + Math.random() * 1.6, start: (i / count) * 0.4, wave: Math.random() * 6.28 });
        }
        part.update = (k) => {
          // the table's left-to-right, whichever way the card is turned
          const yaw = group.rotation.y;
          const wx = Math.cos(yaw);
          const wz = Math.sin(yaw);
          for (const g of gusts) {
            const local = Math.min(1, Math.max(0, (k - g.start) / (1 - g.start)));
            const along = (local - 0.5) * span;
            const side = g.across + Math.sin(local * 5 + g.wave) * 0.5;
            g.sp.position.set(wx * along - wz * side, g.y + Math.sin(local * 7 + g.wave) * 0.35, wz * along + wx * side);
            g.sp.scale.set(long * (0.8 + 0.5 * Math.sin(Math.PI * local)), long * 0.32, 1); // long and low: a streak
            g.sp.material.opacity = local <= 0 ? 0 : strength * Math.sin(Math.PI * local);
          }
        };
        break;
      }
      case 'fall': {
        // flakes (or leaves) come down over the card, swaying as they fall
        const count = layer.count ?? 8;
        const height = layer.size ?? 5;
        const big = layer.dot ?? 0.5;
        const flakes: { sp: THREE.Sprite; x: number; z: number; start: number; sway: number; phase: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending })),
          ) as THREE.Sprite;
          flakes.push({ sp, x: (Math.random() - 0.5) * 7.5, z: (Math.random() - 0.5) * 7.5, start: Math.random() * 0.5, sway: 0.4 + Math.random() * 0.7, phase: Math.random() * 6.28 });
        }
        part.update = (k) => {
          for (const f of flakes) {
            const local = Math.min(1, Math.max(0, (k - f.start) / (1 - f.start)));
            f.sp.position.set(f.x + Math.sin(local * 6 + f.phase) * f.sway, 0.4 + height * (1 - local), f.z + Math.cos(local * 5 + f.phase) * f.sway * 0.5);
            f.sp.scale.set(big, big * (big > 0.7 ? 0.6 : 1), 1); // the bigger ones are leaves: flatter
            f.sp.material.rotation = big > 0.7 ? Math.sin(local * 6 + f.phase) * 0.8 : 0;
            f.sp.material.opacity = local <= 0 ? 0 : strength * Math.min(1, local * 5) * Math.min(1, (1 - local) * 4);
          }
        };
        break;
      }
      case 'twister': {
        // a funnel of wind and cloud spinning up from the ground, wider as it rises, and a ring of wind on the
        // ground round every lane it reaches (follow.userData.ringW: half its width; ringD: half its depth)
        const ringW = typeof follow.userData.ringW === 'number' ? follow.userData.ringW : CW * 1.6;
        const ringD = typeof follow.userData.ringD === 'number' ? follow.userData.ringD : CH * 0.75;
        const ringGeo = new THREE.RingGeometry(0.93, 1, 96);
        ringGeo.rotateX(-Math.PI / 2);
        const ring = add(new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Mesh;
        ring.position.y = 0.12;
        ring.scale.set(ringW, 1, ringD);
        // wind streaks running round the ring
        const streaks: THREE.Mesh[] = [];
        for (let i = 0; i < 18; i++) {
          const m = add(new THREE.Mesh(flatPlane(2.2, 2.2), material(t.dot))) as THREE.Mesh;
          m.position.y = 0.2;
          streaks.push(m);
        }
        const height = layer.size ?? 16;
        const n = layer.count ?? 40;
        const puffs: { sp: THREE.Sprite; h: number; a: number; spin: number }[] = [];
        for (let i = 0; i < n; i++) {
          const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.cloud, color: i % 3 === 0 ? new THREE.Color(colors[Math.min(1, colors.length - 1)]) : color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
          puffs.push({ sp, h: i / n, a: Math.random() * Math.PI * 2, spin: 5 + Math.random() * 3 });
        }
        const D = layer.duration;
        part.update = (k) => {
          const time = k * D;
          const env = Math.min(1, k * 5) * Math.min(1, (1 - k) * 4);
          (ring.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * env * (0.55 + 0.25 * Math.sin(time * 9)));
          streaks.forEach((m, i) => {
            const a = time * 3.2 + (i / streaks.length) * Math.PI * 2;
            m.position.x = Math.cos(a) * ringW * 0.965;
            m.position.z = Math.sin(a) * ringD * 0.965;
            (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * env * 0.8);
          });
          // the funnel grows up out of the ground, then thins away
          const grow = Math.min(1, k * 3);
          for (const p of puffs) {
            const h = (p.h + time * 0.35) % 1; // (the cloud climbs the funnel and starts again at the bottom)
            const r = 1.2 + h * h * 7.5;
            const a = p.a + time * p.spin * (1.3 - h * 0.6);
            const sway = Math.sin(time * 2.1 + h * 2) * 0.9 * h;
            p.sp.position.set(Math.cos(a) * r + sway, 0.4 + h * height * grow, Math.sin(a) * r * 0.85);
            const sc = 2.2 + h * 5;
            p.sp.scale.set(sc, sc, 1);
            p.sp.material.opacity = Math.min(1, strength * env * (0.25 + 0.45 * (1 - Math.abs(h - 0.5))));
          }
        };
        break;
      }
      case 'doors': {
        // a pair of see-through doors of light standing over the card, swinging open from the middle and fading
        // (Arcane Doors)
        const W = CW * 0.5, H = CH * 0.85;
        const glowGeo = flatPlane(CW * 0.9, CH * 0.9);
        const spill = add(new THREE.Mesh(glowGeo, material(t.soft))) as THREE.Mesh;
        spill.position.y = 0.1;
        const doors = [-1, 1].map((sd) => {
          const pivot = add(new THREE.Group());
          pivot.position.set(sd * W, 0.2, 0);
          const pane = new THREE.Mesh(new THREE.PlaneGeometry(W, H), new THREE.MeshBasicMaterial({ map: t.frame, color, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending }));
          pane.position.set(-sd * W / 2, H / 2, 0);
          const fill = new THREE.Mesh(new THREE.PlaneGeometry(W * 0.9, H * 0.94), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending }));
          fill.position.copy(pane.position);
          pivot.add(pane, fill);
          return { pivot, pane, fill, sd };
        });
        part.update = (k) => {
          const open = ease(Math.min(1, Math.max(0, (k - 0.15) / 0.55)));
          const fade = Math.min(1, k * 6) * Math.min(1, (1 - k) * 3);
          for (const d of doors) {
            d.pivot.rotation.y = d.sd * open * 1.95;
            (d.pane.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * 0.9 * fade);
            (d.fill.material as THREE.MeshBasicMaterial).opacity = strength * 0.22 * fade;
          }
          (spill.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * 0.6 * open * fade);
        };
        break;
      }
      case 'banner': {
        // a battle banner planted beside the card: a dark pole with a gold finial, and a long banner (its side's color) hanging
        // from a crossbar, its tail cut in a swallowtail, with a pale gold border and a skull. It rises out of the
        // ground, waves in the wind, and fades away (Undead Legion's General)
        const sz = layer.size ?? 1;
        const bw = CW * 0.75 * sz, bh = CH * 1.0 * sz, pole = bh * 1.65;
        const cv = document.createElement('canvas');
        cv.width = 128; cv.height = 256;
        const g = cv.getContext('2d')!;
        const cloth = new THREE.Color(colors[0]).getStyle(), trim = new THREE.Color(colors[Math.min(1, colors.length - 1)]).getStyle();
        const shape = () => { g.beginPath(); g.moveTo(4, 4); g.lineTo(124, 4); g.lineTo(124, 252); g.lineTo(64, 206); g.lineTo(4, 252); g.closePath(); };
        shape(); g.fillStyle = cloth; g.fill();
        const shade = g.createLinearGradient(0, 0, 128, 0); shade.addColorStop(0, 'rgba(0,0,0,0.35)'); shade.addColorStop(0.5, 'rgba(255,255,255,0.08)'); shade.addColorStop(1, 'rgba(0,0,0,0.35)');
        g.fillStyle = shade; shape(); g.fill();
        g.lineWidth = 7; g.strokeStyle = trim; shape(); g.stroke();
        // a skull in pale gold
        g.fillStyle = trim;
        g.beginPath(); g.arc(64, 96, 26, 0, Math.PI * 2); g.fill();
        g.fillRect(48, 112, 32, 22);
        g.fillStyle = cloth;
        g.beginPath(); g.ellipse(53, 97, 7, 9, 0, 0, Math.PI * 2); g.ellipse(75, 97, 7, 9, 0, 0, Math.PI * 2); g.fill();
        g.fillRect(56, 124, 3, 10); g.fillRect(63, 124, 3, 10); g.fillRect(70, 124, 3, 10);
        // crossed bones under it
        g.strokeStyle = trim; g.lineWidth = 6; g.lineCap = 'round';
        g.beginPath(); g.moveTo(36, 146); g.lineTo(92, 176); g.moveTo(92, 146); g.lineTo(36, 176); g.stroke();
        const tex = new THREE.CanvasTexture(cv);
        tex.colorSpace = THREE.SRGBColorSpace;
        const geo = new THREE.PlaneGeometry(bw, bh, 12, 1);
        const base = Float32Array.from(geo.attributes.position.array as Float32Array);
        const flag = add(new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false }))) as THREE.Mesh;
        const stick = add(new THREE.Mesh(new THREE.CylinderGeometry(0.12 * sz, 0.14 * sz, pole, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(colors[Math.min(2, colors.length - 1)]), transparent: true, opacity: 0 }))) as THREE.Mesh;
        const bar = add(new THREE.Mesh(new THREE.CylinderGeometry(0.08 * sz, 0.08 * sz, bw * 1.1, 6), new THREE.MeshBasicMaterial({ color: new THREE.Color(colors[Math.min(2, colors.length - 1)]), transparent: true, opacity: 0 }))) as THREE.Mesh;
        bar.rotation.z = Math.PI / 2;
        const tip = add(new THREE.Mesh(new THREE.ConeGeometry(0.28 * sz, 0.7 * sz, 6), new THREE.MeshBasicMaterial({ color: new THREE.Color(colors[Math.min(1, colors.length - 1)]), transparent: true, opacity: 0 }))) as THREE.Mesh;
        // it stands at the card's far left corner, upright
        const px = -CW * 0.62, pz = -CH * 0.42;
        part.update = (k, time) => {
          const env = Math.min(1, k * 5) * Math.min(1, (1 - k) * 4);
          const up = 1 - (1 - Math.min(1, k * 4)) ** 3; // it rises out of the ground
          const h = pole * up;
          stick.position.set(px, h / 2, pz); stick.scale.set(1, Math.max(0.01, up), 1);
          bar.position.set(px + bw * 0.5, h - 0.35 * sz, pz);
          tip.position.set(px, h + 0.3 * sz, pz);
          flag.position.set(px + bw * 0.52, h - 0.35 * sz - bh / 2, pz);
          // it waves: a ripple along its length, stronger toward its free end
          const pos = geo.attributes.position.array as Float32Array;
          for (let i = 0; i < pos.length; i += 3) {
            const u = (base[i] + bw / 2) / bw;
            pos[i + 2] = base[i + 2] + Math.sin(time * 5 - u * 5) * 0.55 * sz * u;
          }
          geo.attributes.position.needsUpdate = true;
          for (const m of [flag, stick, bar, tip]) (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * env);
        };
        break;
      }
      case 'vines': {
        // seaweed vines wrapped round the card's edge: wavy strands of overlapping beads creeping along the
        // outline, swaying a little as if under water, with small leaves along them (Tangleweed's Lock)
        const strands = layer.count ?? 5;
        const W = CW * 1.0, H = CH * 1.0, P = 2 * (W + H);
        const along = (u: number) => {
          let d = (((u % 1) + 1) % 1) * P;
          if (d < W) return { x: -W / 2 + d, z: -H / 2, nx: 0, nz: -1 };
          d -= W; if (d < H) return { x: W / 2, z: -H / 2 + d, nx: 1, nz: 0 };
          d -= H; if (d < W) return { x: W / 2 - d, z: H / 2, nx: 0, nz: 1 };
          d -= W; return { x: -W / 2, z: H / 2 - d, nx: -1, nz: 0 };
        };
        const beads: { sp: THREE.Sprite; u: number; phase: number; amp: number; at: number; leaf: boolean }[] = [];
        const per = 44;
        const big = layer.dot ?? 0.75;
        for (let k = 0; k < strands; k++) {
          const start = k / strands + Math.random() * 0.05;
          const amp = 0.22 + Math.random() * 0.18;
          const phase = Math.random() * 6.28;
          for (let i = 0; i < per; i++) {
            const leaf = i % 6 === 3;
            const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: new THREE.Color(colors[Math.min(leaf ? 1 : (i % 3 === 0 ? 2 : 0), colors.length - 1)]), transparent: true, opacity: 0, depthWrite: false }))) as THREE.Sprite;
            beads.push({ sp, u: start + (i / per) * (1.15 / strands), phase, amp, at: i / per, leaf });
          }
        }
        part.update = (k) => {
          const grow = Math.min(1, k * 3); // they creep round the edge as it is Locked
          const fade = Math.min(1, (1 - k) * 4);
          for (const b of beads) {
            const p = along(b.u);
            const wave = Math.sin(b.at * 18 + b.phase + k * 3) * b.amp;
            b.sp.position.set(p.x + p.nx * wave, 0.35 + (b.leaf ? 0.15 : 0), p.z + p.nz * wave);
            const sz = b.leaf ? big * 1.6 : big * (0.9 + 0.25 * Math.sin(b.at * 9 + b.phase));
            b.sp.scale.set(sz, b.leaf ? sz * 0.5 : sz, 1); // (a leaf: long and thin)
            if (b.leaf) b.sp.material.rotation = b.phase + Math.sin(k * 3 + b.at * 10) * 0.5;
            b.sp.material.opacity = b.at <= grow ? Math.min(1, strength * fade) : 0;
          }
        };
        break;
      }
      case 'halo': {
        // a glowing ring round the lane's outline, breathing softly (a lane a Spell is working on)
        const size = layer.size ?? 1.12;
        const m = add(new THREE.Mesh(flatPlane(CW * 1.3 * size, CH * 1.3 * size), material(t.ring))) as THREE.Mesh;
        m.position.y = 0.08;
        const pulses = layer.pulses ?? 1;
        part.update = (k) => {
          const env = Math.min(1, k * 4) * Math.min(1, (1 - k) * 4);
          const beat = 0.75 + 0.25 * Math.sin(Math.PI * 2 * pulses * k);
          (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * env * beat);
        };
        break;
      }
      case 'rimpuff': {
        // small clouds hang all round the lane's border, drifting slowly along it (dark: black smoke)
        const count = layer.count ?? 10;
        const big = layer.dot ?? 2.6;
        const clouds: { sp: THREE.Sprite; u: number; speed: number; bob: number; turn: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.cloud, color: layer.dark ? new THREE.Color(color).multiplyScalar(0.5) : color, transparent: true, opacity: 0, depthWrite: false, blending: layer.dark ? THREE.NormalBlending : THREE.AdditiveBlending }))) as THREE.Sprite;
          clouds.push({ sp, u: i / count + Math.random() * 0.03, speed: 0.04 + Math.random() * 0.04, bob: Math.random() * 6.28, turn: Math.random() * 6.28 });
        }
        // a point on the lane's outline, u = 0…1 round it
        const W = CW * 1.02, H = CH * 1.02, P = 2 * (W + H);
        const along = (u: number) => {
          let d = (((u % 1) + 1) % 1) * P;
          if (d < 2 * W) return d < W ? { x: -W / 2 + d, z: -H / 2 } : { x: W / 2 - (d - W), z: H / 2 };
          d -= 2 * W;
          return d < H ? { x: W / 2, z: -H / 2 + d } : { x: -W / 2, z: H / 2 - (d - H) };
        };
        part.update = (k) => {
          const env = Math.min(1, k * 3) * Math.min(1, (1 - k) * 3);
          for (const c of clouds) {
            const p = along(c.u + k * c.speed);
            c.sp.position.set(p.x, 0.7 + 0.35 * Math.sin(k * 6 + c.bob), p.z);
            const sz = big * (0.85 + 0.2 * Math.sin(k * 4 + c.bob));
            c.sp.scale.set(sz, sz, 1);
            c.sp.material.rotation = c.turn + k * 0.5;
            c.sp.material.opacity = Math.min(1, strength * env);
          }
        };
        break;
      }
      case 'rocks': {
        // small rocks lie scattered on the floor of the lane, each with a glowing edge (Slag Vial, Earthquake)
        const count = layer.count ?? 7;
        const big = layer.dot ?? 1.3;
        const stones: { m: THREE.Mesh; rim: THREE.Mesh }[] = [];
        for (let i = 0; i < count; i++) {
          const w = big * (0.6 + Math.random() * 0.7);
          const x = (Math.random() - 0.5) * CW * 0.85, z = (Math.random() - 0.5) * CH * 0.85;
          const rim = add(new THREE.Mesh(flatPlane(w * 1.7, w * 1.4), material(t.dot))) as THREE.Mesh;
          rim.position.set(x, 0.07, z);
          const m = add(new THREE.Mesh(new THREE.CircleGeometry(w / 2, 5 + Math.floor(Math.random() * 3)).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0x1a1210, transparent: true, opacity: 0, depthWrite: false }))) as THREE.Mesh;
          m.position.set(x, 0.09, z);
          m.rotation.y = Math.random() * 6.28;
          m.scale.set(1, 1, 0.7 + Math.random() * 0.4);
          stones.push({ m, rim });
        }
        part.update = (k) => {
          const env = Math.min(1, k * 4) * Math.min(1, (1 - k) * 4);
          for (const st of stones) {
            (st.m.material as THREE.MeshBasicMaterial).opacity = env * 0.95;
            (st.rim.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * env * (0.7 + 0.3 * Math.sin(k * 8)));
          }
        };
        break;
      }
      case 'pit': {
        // a dark hole open in the middle of the lane (Tectonic Shift: nothing can be summoned here)
        const size = layer.size ?? 0.55;
        const m = add(new THREE.Mesh(flatPlane(CW * size * 1.4, CW * size * 1.4), new THREE.MeshBasicMaterial({ map: t.dot, color: 0x000000, transparent: true, opacity: 0, depthWrite: false }))) as THREE.Mesh;
        const rim = add(new THREE.Mesh(flatPlane(CW * size * 1.7, CW * size * 1.7), material(t.circle))) as THREE.Mesh;
        m.position.y = 0.1; rim.position.y = 0.09;
        part.update = (k) => {
          const env = Math.min(1, k * 4) * Math.min(1, (1 - k) * 4);
          (m.material as THREE.MeshBasicMaterial).opacity = env;
          (rim.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * env * 0.8);
        };
        break;
      }
      case 'planets': {
        // a small, unknown planetary system hanging above the card: a pale star, and planets (colors 1 on) going round
        // it on tilted orbits, the inner ones faster, each with a faint trail; the largest has a moon. Where they are
        // comes from the clock, not from when this round began, so one round fades into the next without a jump.
        const lift = layer.size ?? 4.2; // how high above the card the system hangs
        const star = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
        const core = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: new THREE.Color('#ffffff'), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
        star.position.set(0, lift, 0);
        core.position.set(0, lift, 0);
        const pick = (i: number) => new THREE.Color(colors[Math.min(1 + (i % Math.max(1, colors.length - 1)), colors.length - 1)]);
        const specs = [
          { r: 1.9, size: 0.7, speed: 1.15, phase: 0.4 },
          { r: 3.0, size: 1.1, speed: 0.72, phase: 2.6 },
          { r: 4.3, size: 1.5, speed: 0.46, phase: 4.4, moon: true },
          { r: 5.4, size: 0.9, speed: 0.31, phase: 1.3 },
        ].slice(0, Math.max(1, Math.min(4, layer.count ?? 4)));
        const TRAIL = 6;
        const tilt = 0.32; // how far the orbits lean out of the table's plane
        const bodies = specs.map((sp, i) => {
          const c = pick(i);
          const mk = (map: THREE.Texture, col: THREE.Color) => add(new THREE.Sprite(new THREE.SpriteMaterial({ map, color: col, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
          return { ...sp, body: mk(t.dot, c), halo: mk(t.dot, c), trail: Array.from({ length: TRAIL }, () => mk(t.dot, c)), moon: sp.moon ? mk(t.dot, new THREE.Color('#ffffff')) : null };
        });
        const where = (r: number, a: number) => ({ x: Math.cos(a) * r, z: Math.sin(a) * r * 0.62, y: lift + Math.sin(a) * r * tilt });
        part.update = (k) => {
          const fade = 0.07; // (short: with the next round starting a second early, the two cross over at full strength)
          const env = k < fade ? k / fade : k > 1 - fade ? (1 - k) / fade : 1;
          const e = Math.max(0, Math.min(1, env)) * strength;
          const clock = performance.now() / 1000;
          const breathe = 1 + 0.08 * Math.sin(clock * 2.1);
          star.scale.set(3.6 * breathe, 3.6 * breathe, 1);
          star.material.opacity = 0.6 * e;
          core.scale.set(0.9, 0.9, 1);
          core.material.opacity = 0.95 * e;
          for (const b of bodies) {
            const a = b.phase + clock * b.speed;
            const p = where(b.r, a);
            b.body.position.set(p.x, p.y, p.z);
            b.body.scale.set(b.size, b.size, 1);
            // (the far side of its orbit is a little dimmer, so it reads as going round behind the star)
            const depth = 0.7 + 0.3 * (0.5 + 0.5 * Math.sin(a));
            b.body.material.opacity = e * depth;
            b.halo.position.copy(b.body.position);
            b.halo.scale.set(b.size * 2.6, b.size * 2.6, 1);
            b.halo.material.opacity = 0.35 * e * depth;
            b.trail.forEach((d, j) => {
              const q = where(b.r, a - (j + 1) * 0.11);
              d.position.set(q.x, q.y, q.z);
              const f = 1 - (j + 1) / (TRAIL + 1);
              d.scale.set(b.size * 0.55 * f, b.size * 0.55 * f, 1);
              d.material.opacity = 0.45 * e * f * depth;
            });
            if (b.moon) {
              const m = clock * 2.4;
              b.moon.position.set(p.x + Math.cos(m) * 0.9, p.y + Math.sin(m) * 0.25, p.z + Math.sin(m) * 0.55);
              b.moon.scale.set(0.45, 0.45, 1);
              b.moon.material.opacity = 0.85 * e * depth;
            }
          }
        };
        break;
      }
      case 'twinkle': {
        // glints wink in and out, here and there above the card
        const count = layer.count ?? 6;
        const big = layer.dot ?? 0.9;
        const stars: { sp: THREE.Sprite; start: number; length: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(new THREE.SpriteMaterial({ map: t.spark, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending })),
          ) as THREE.Sprite;
          const spread = layer.size ?? 8; // how wide an area they wink in (a growing aura spreads them further)
          sp.position.set((Math.random() - 0.5) * spread, 0.8 + Math.random() * 3.2 * Math.max(1, spread / 8), (Math.random() - 0.5) * spread);
          stars.push({ sp, start: (i / count) * 0.7 + Math.random() * 0.05, length: 0.22 + Math.random() * 0.12 });
        }
        part.update = (k) => {
          for (const s of stars) {
            const local = (k - s.start) / s.length;
            const on = local <= 0 || local >= 1 ? 0 : Math.sin(Math.PI * local);
            const size = big * (0.4 + 0.9 * on);
            s.sp.scale.set(size, size, 1);
            s.sp.material.rotation = local * 0.8;
            s.sp.material.opacity = strength * on;
          }
        };
        break;
      }
      case 'beam': {
        // a slim, steady column of light on the card. It fades in and out at its two ends, so a round
        // started just before the last one finishes takes over without a seam.
        const height = layer.size ?? 9;
        const sp = add(
          new THREE.Sprite(
            new THREE.SpriteMaterial({ map: t.column, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }),
          ),
        ) as THREE.Sprite;
        sp.center.set(0.5, 0);
        sp.position.y = 0.1;
        part.update = (k, time) => {
          const edge = Math.min(1, k / BEAM_FADE, (1 - k) / BEAM_FADE);
          sp.scale.set(CW * (0.78 + 0.05 * Math.sin(time * 2.3)), height * (0.94 + 0.06 * Math.sin(time * 1.7)), 1);
          sp.material.opacity = Math.max(0, strength * edge * (0.86 + 0.14 * Math.sin(time * 3.1)));
        };
        break;
      }
      case 'pillar': {
        // a soft column of light standing on the card
        const height = layer.size ?? 9;
        const sp = add(
          new THREE.Sprite(
            new THREE.SpriteMaterial({ map: t.column, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }),
          ),
        ) as THREE.Sprite;
        sp.center.set(0.5, 0);
        sp.position.y = 0.1;
        part.update = (k) => {
          const grow = ease(Math.min(1, k * 2.2));
          sp.scale.set(CW * (1.25 - 0.35 * k), height * grow, 1);
          sp.material.opacity = Math.min(1, strength * Math.pow(Math.sin(Math.PI * Math.min(1, k * 1.02)), 0.7));
        };
        break;
      }
      case 'streak': {
        // quick diagonal slashes of light just above the card
        const count = layer.count ?? 3;
        const length = CH * (layer.size ?? 1.5);
        const streaks: { m: THREE.Mesh; offset: number; start: number }[] = [];
        for (let i = 0; i < count; i++) {
          const m = add(new THREE.Mesh(flatPlane(length, 0.55), material(t.dot))) as THREE.Mesh;
          m.rotation.y = layer.flip ? -0.62 : 0.62; // slash angle
          streaks.push({ m, offset: (i - (count - 1) / 2) * 2.3, start: i * 0.16 });
        }
        const dir = new THREE.Vector3(Math.cos(layer.flip ? 0.62 : -0.62), 0, Math.sin(layer.flip ? 0.62 : -0.62));
        const across = new THREE.Vector3(-dir.z, 0, dir.x);
        part.update = (k) => {
          for (const st of streaks) {
            const span = 1 - (count - 1) * 0.16;
            const local = Math.min(1, Math.max(0, (k - st.start) / span));
            const travel = (ease(local) - 0.5) * CH * 1.5;
            st.m.position.copy(dir).multiplyScalar(travel).addScaledVector(across, st.offset);
            st.m.position.y = 1.4;
            (st.m.material as THREE.MeshBasicMaterial).opacity = local <= 0 || local >= 1 ? 0 : Math.min(1, strength * Math.sin(Math.PI * local));
          }
        };
        break;
      }
      case 'cracks': {
        // cracks spread out from the middle of the card: a dark line with a glowing edge
        const fit = CONFIG.playEffects.crackScale; // a little inside the card, so the cracks don't hang over its edges
        const glow = add(new THREE.Mesh(flatPlane(CW * 1.02 * fit, CH * 1.02 * fit), material(t.cracks))) as THREE.Mesh;
        const dark = add(
          new THREE.Mesh(
            flatPlane(CW * fit, CH * fit),
            new THREE.MeshBasicMaterial({ map: t.cracks, color: layer.line !== undefined ? new THREE.Color(colors[Math.min(layer.line, colors.length - 1)]) : 0x0a0306, transparent: true, opacity: 0, depthWrite: false }),
          ),
        ) as THREE.Mesh;
        glow.position.y = 0.05;
        dark.position.y = 0.06;
        part.update = (k) => {
          if (layer.hold) {
            const on = Math.min(1, k * 4) * Math.min(1, (1 - k) * 4);
            const sc = layer.scale ?? 1;
            glow.scale.set(1.03 * sc, 1, 1.03 * sc);
            dark.scale.set(sc, 1, sc);
            (glow.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * on * (0.8 + 0.2 * Math.sin(k * 9)));
            (dark.material as THREE.MeshBasicMaterial).opacity = on * 0.9;
            return;
          }
          const spread = 0.25 + 0.75 * ease(Math.min(1, k * 1.25));
          glow.scale.set(spread * 1.03, 1, spread * 1.03);
          dark.scale.set(spread, 1, spread);
          const shown = Math.min(1, k * 4);
          (glow.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * shown * (0.75 + 0.25 * Math.sin(k * 40)));
          (dark.material as THREE.MeshBasicMaterial).opacity = shown;
        };
        break;
      }
      case 'shards': {
        // the card bursts: dark fragments fly outward, tumbling, and fall away
        const count = layer.count ?? 16;
        const reach = layer.size ?? 10;
        const shards: { m: THREE.Mesh; x: number; z: number; dx: number; dz: number; up: number; spin: THREE.Vector3 }[] = [];
        for (let i = 0; i < count; i++) {
          const w = (0.8 + Math.random() * 1.4) * (layer.strength !== undefined && layer.dot !== undefined ? 0.6 : 1); // (a token's shards are smaller)
          const shape = new THREE.Shape([new THREE.Vector2(-w / 2, -w * 0.3), new THREE.Vector2(w / 2, -w * (0.15 + Math.random() * 0.3)), new THREE.Vector2((Math.random() - 0.5) * w * 0.6, w * 0.5)]);
          const tone = i % 3 === 0 ? color.clone() : color.clone().multiplyScalar(0.28 + Math.random() * 0.2);
          const m = add(
            new THREE.Mesh(new THREE.ShapeGeometry(shape), new THREE.MeshBasicMaterial({ color: tone, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide })),
          ) as THREE.Mesh;
          const from = layer.dot ?? 0.9; // how much of the card they start from (a small token: a little of it)
          const x = (Math.random() - 0.5) * CW * from;
          const z = (Math.random() - 0.5) * CH * from;
          const away = Math.atan2(z, x) + (Math.random() - 0.5) * 0.9;
          const far = reach * (0.45 + Math.random() * 0.65);
          shards.push({ m, x, z, dx: Math.cos(away) * far, dz: Math.sin(away) * far, up: 2.5 + Math.random() * 5, spin: new THREE.Vector3(Math.random() * 9 - 4.5, Math.random() * 9 - 4.5, Math.random() * 9 - 4.5) });
        }
        part.update = (k) => {
          const e = ease(k);
          for (const sh of shards) {
            sh.m.position.set(sh.x + sh.dx * e, 0.3 + Math.sin(Math.PI * Math.min(1, k * 1.15)) * sh.up, sh.z + sh.dz * e);
            sh.m.rotation.set(-Math.PI / 2 + sh.spin.x * k, sh.spin.y * k, sh.spin.z * k);
            (sh.m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, (1 - k) * 2.2);
          }
        };
        break;
      }
      case 'shake': {
        let fired = false;
        part.update = () => {
          if (fired) return;
          fired = true;
          this.onShake(strength, layer.duration);
        };
        break;
      }
      case 'seep': {
        // blood wells up in a few spots on the card's face and runs down it in thin trails (toward the
        // bottom of the card's picture), each ending in a drop, then soaks away. It is painted over the
        // card, not added to it as light, so it reads as dark and wet.
        const count = layer.count ?? 4;
        const run = layer.size ?? 4; // how far a trail runs at most (cm)
        const big = layer.dot ?? 1.5; // how wide a pool gets (cm)
        const paint = (map: THREE.Texture, tone: THREE.Color) =>
          new THREE.MeshBasicMaterial({ map, color: tone, transparent: true, opacity: 0, depthWrite: false });
        const heartTone = color.clone().multiplyScalar(0.5);
        const spots: { pool: THREE.Mesh; heart: THREE.Mesh; trail: THREE.Mesh; bead: THREE.Mesh; x: number; z: number; size: number; len: number; start: number }[] = [];
        for (let i = 0; i < count; i++) {
          const x = (Math.random() - 0.5) * CW * 0.74;
          const z = -CH / 2 + CH * (0.1 + Math.random() * 0.45); // the upper half or so: room to run down
          const pool = add(new THREE.Mesh(flatPlane(1, 1), paint(t.cloud, color))) as THREE.Mesh;
          const heart = add(new THREE.Mesh(flatPlane(1, 1), paint(t.cloud, heartTone))) as THREE.Mesh;
          const trail = add(new THREE.Mesh(flatPlane(1, 1), paint(t.drip, color))) as THREE.Mesh;
          const bead = add(new THREE.Mesh(flatPlane(1, 1), paint(t.dot, color))) as THREE.Mesh;
          pool.position.set(x, 0.07, z);
          heart.position.set(x, 0.072, z);
          pool.rotation.y = heart.rotation.y = Math.random() * 6.28;
          const room = CH / 2 * 0.93 - z; // a trail never runs off the bottom of the card
          spots.push({ pool, heart, trail, bead, x, z, size: big * (0.65 + Math.random() * 0.5), len: Math.min(room, run * (0.45 + Math.random() * 0.6)), start: (i / count) * 0.3 + Math.random() * 0.08 });
        }
        part.update = (k) => {
          for (const sp of spots) {
            const local = Math.min(1, Math.max(0, (k - sp.start) / (1 - sp.start)));
            const shown = local <= 0 ? 0 : strength * Math.min(1, local * 6) * (local > 0.7 ? Math.max(0, 1 - (local - 0.7) / 0.3) : 1);
            const swell = sp.size * ease(Math.min(1, local / 0.35));
            sp.pool.scale.set(Math.max(0.01, swell), 1, Math.max(0.01, swell * 0.85));
            sp.heart.scale.set(Math.max(0.01, swell * 0.55), 1, Math.max(0.01, swell * 0.45));
            // the trail starts running once the pool has welled up, quick at first, slowing as it thins out
            const ran = sp.len * ease(Math.min(1, Math.max(0, (local - 0.15) / 0.55)));
            const width = 0.22 + sp.size * 0.08;
            sp.trail.position.set(sp.x, 0.074, sp.z + ran / 2);
            sp.trail.scale.set(width, 1, Math.max(0.01, ran));
            sp.bead.position.set(sp.x, 0.076, sp.z + ran);
            sp.bead.scale.set(width * 2.1, 1, width * 2.6);
            (sp.pool.material as THREE.MeshBasicMaterial).opacity = Math.min(1, shown * 1.15);
            (sp.heart.material as THREE.MeshBasicMaterial).opacity = Math.min(1, shown * 0.9);
            (sp.trail.material as THREE.MeshBasicMaterial).opacity = Math.min(1, shown * 0.9 * Math.min(1, ran * 3));
            (sp.bead.material as THREE.MeshBasicMaterial).opacity = Math.min(1, shown * Math.min(1, ran * 3));
          }
        };
        break;
      }
      case 'wisp': {
        // thin curls of smoke rise off the card, each a short trail that winds as it climbs and frays out at
        // the top (dark: painted over the table, so the curls can be near-black)
        const count = layer.count ?? 3;
        const height = layer.size ?? 6;
        const big = layer.dot ?? 1.2;
        const seg = 16; // puffs in each curl, head first
        const wisps: { puffs: THREE.Sprite[]; x: number; z: number; start: number; phase: number; turn: number; amp: number }[] = [];
        for (let i = 0; i < count; i++) {
          const puffs: THREE.Sprite[] = [];
          for (let j = 0; j < seg; j++) {
            puffs.push(
              add(
                new THREE.Sprite(
                  new THREE.SpriteMaterial({ map: t.cloud, color, transparent: true, opacity: 0, depthWrite: false, blending: layer.dark ? THREE.NormalBlending : THREE.AdditiveBlending }),
                ),
              ) as THREE.Sprite,
            );
          }
          wisps.push({ puffs, x: (Math.random() - 0.5) * CW * 0.7, z: (Math.random() - 0.5) * CH * 0.6, start: (i / count) * 0.4 + Math.random() * 0.1, phase: Math.random() * 6.28, turn: (2.4 + Math.random() * 2) * (i % 2 ? 1 : -1), amp: 0.6 + Math.random() * 0.6 });
        }
        part.update = (k) => {
          for (const w of wisps) {
            const local = (k - w.start) / (1 - w.start);
            const life = local <= 0 || local >= 1 ? 0 : Math.pow(Math.sin(Math.PI * local), 0.7);
            w.puffs.forEach((sp, j) => {
              const s = local - j * 0.028; // each puff follows a little behind the one before it: a thin ribbon
              if (s <= 0 || s >= 1 || !life) {
                sp.material.opacity = 0;
                return;
              }
              const wind = w.amp * (0.25 + s);
              sp.position.set(w.x + Math.sin(s * w.turn + w.phase) * wind, 0.4 + height * (1 - Math.pow(1 - s, 1.4)), w.z + Math.cos(s * w.turn * 0.8 + w.phase) * wind * 0.5);
              const along = j / (seg - 1); // 0 at the head, 1 at the tail: both ends fray out
              const body = Math.sin(Math.PI * (0.08 + along * 0.84));
              const size = big * (0.55 + 0.7 * s) * (0.55 + 0.45 * body);
              sp.scale.set(size, size, 1);
              sp.material.rotation = w.phase + s * 2;
              sp.material.opacity = strength * 0.6 * life * body * (1 - s * 0.6);
            });
          }
        };
        break;
      }
      case 'sheen': {
        // a slanted band of light sweeps across the card's face, left to right, like light catching a gem.
        // It is cut to the card's own shape, so it never spills onto the table.
        const geo = cardPlane(CW, CH);
        geo.rotateX(-Math.PI / 2);
        const map = t.sheen.clone(); // its own copy, slid along on its own
        map.needsUpdate = true;
        const mat = material(map);
        mat.userData.ownMap = true; // disposed with the effect
        const m = add(new THREE.Mesh(geo, mat)) as THREE.Mesh;
        m.position.y = 0.08;
        part.update = (k) => {
          map.offset.x = 1 - 2 * (k * k * (3 - 2 * k)); // the band enters at the left edge and leaves at the right
          mat.opacity = Math.min(1, strength * Math.pow(Math.sin(Math.PI * k), 0.4));
        };
        break;
      }
      case 'rumble': {
        // the card itself trembles. The table reads `rumble` off the card and jolts it by that much each frame.
        part.update = (k) => {
          follow.userData.rumble = k >= 1 ? 0 : strength * Math.pow(Math.sin(Math.PI * k), 0.5) * (0.7 + 0.3 * Math.sin(k * 29));
        };
        break;
      }
      case 'glide': {
        // streaks of light race across just above the card, one after another: speed
        const count = layer.count ?? 4;
        const length = CW * (layer.size ?? 1.8);
        const streaks: { m: THREE.Mesh; z: number; y: number; start: number; span: number; len: number; thick: number }[] = [];
        for (let i = 0; i < count; i++) {
          const m = add(new THREE.Mesh(flatPlane(1, 1), material(t.trail))) as THREE.Mesh;
          streaks.push({ m, z: (Math.random() - 0.5) * CH * 0.85, y: 1 + Math.random() * 2.2, start: (i / count) * 0.55 + Math.random() * 0.08, span: 0.32 + Math.random() * 0.1, len: length * (0.7 + Math.random() * 0.5), thick: 0.3 + Math.random() * 0.2 });
        }
        part.update = (k) => {
          for (const st of streaks) {
            const local = (k - st.start) / st.span;
            const mat = st.m.material as THREE.MeshBasicMaterial;
            if (local <= 0 || local >= 1) {
              mat.opacity = 0;
              continue;
            }
            const on = Math.sin(Math.PI * local);
            const len = st.len * (0.45 + 0.55 * on);
            st.m.position.set(-CW * 1.15 + CW * 2.3 * local, st.y, st.z);
            st.m.scale.set(len, 1, st.thick);
            mat.opacity = Math.min(1, strength * on);
          }
        };
        break;
      }
      case 'riser': {
        // thin streaks shoot up off the face of the card, quickening as they climb: power welling up
        const count = layer.count ?? 6;
        const height = layer.size ?? 7;
        const risers: { sp: THREE.Sprite; x: number; z: number; start: number; span: number; h: number; w: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(new THREE.SpriteMaterial({ map: t.riser, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending })),
          ) as THREE.Sprite;
          sp.center.set(0.5, 0.92); // its head is where it is; the tail hangs below
          risers.push({ sp, x: (Math.random() - 0.5) * CW * 0.85, z: (Math.random() - 0.5) * CH * 0.85, start: (i / count) * 0.5 + Math.random() * 0.1, span: 0.38 + Math.random() * 0.2, h: height * (0.6 + Math.random() * 0.5), w: 0.32 + Math.random() * 0.18 });
        }
        part.update = (k) => {
          for (const r of risers) {
            const local = (k - r.start) / r.span;
            if (local <= 0 || local >= 1) {
              r.sp.material.opacity = 0;
              continue;
            }
            const climb = Math.pow(local, 1.7); // slow off the card, faster and faster
            r.sp.position.set(r.x, 0.3 + r.h * climb, r.z);
            r.sp.scale.set(r.w, r.w * (2 + 9 * local), 1); // the faster it goes, the longer its tail
            r.sp.material.opacity = Math.min(1, strength * Math.min(1, local * 5) * Math.pow(1 - local, 0.7));
          }
        };
        break;
      }
      case 'bolt': {
        // little forks of lightning leap off the card's edges, each strike lasting a blink before another
        // jumps from somewhere else, with a bright spot where it leaves the card
        const count = layer.count ?? 3;
        const reach = layer.size ?? 4;
        const blink = (s: number) => s / layer.duration; // seconds → a share of this layer's time
        const forks: { sp: THREE.Sprite; spot: THREE.Sprite; strikes: [number, number][]; at: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(new THREE.SpriteMaterial({ map: t.bolts[0], color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending })),
          ) as THREE.Sprite;
          sp.center.set(0, 0.5); // the fork's foot is where it is
          const spot = add(
            new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending })),
          ) as THREE.Sprite;
          const strikes: [number, number][] = [];
          for (let at = Math.random() * 0.2; at < 0.9; ) {
            const long = blink(0.07 + Math.random() * 0.09);
            strikes.push([at, at + long]);
            at += long + blink(0.12) + Math.random() * 0.25;
          }
          forks.push({ sp, spot, strikes, at: -1 });
        }
        part.update = (k) => {
          for (const f of forks) {
            const now = f.strikes.findIndex(([a, b]) => k >= a && k < b);
            if (now < 0) {
              f.sp.material.opacity = f.spot.material.opacity = 0;
              continue;
            }
            if (now !== f.at) {
              // a new strike: somewhere else on the card's outline, pointing another way, another shape
              f.at = now;
              const side = Math.floor(Math.random() * 4);
              const along = Math.random() * 2 - 1;
              const x = (side < 2 ? along : side === 2 ? -1 : 1) * (CW / 2) * 0.95;
              const z = (side < 2 ? (side === 0 ? -1 : 1) : along) * (CH / 2) * 0.95;
              f.sp.position.set(x, 0.3 + Math.random() * 0.6, z);
              f.spot.position.copy(f.sp.position);
              f.sp.material.map = t.bolts[Math.floor(Math.random() * t.bolts.length)];
              f.sp.material.rotation = Math.PI * (0.12 + Math.random() * 0.76); // upward, leaning left or right
              const len = reach * (0.5 + Math.random() * 0.6);
              f.sp.scale.set(len, len * 0.375, 1);
              f.spot.scale.set(1.3, 1.3, 1);
            }
            const flicker = 0.65 + 0.35 * Math.random();
            f.sp.material.opacity = Math.min(1, strength * flicker);
            f.spot.material.opacity = Math.min(1, strength * flicker * 0.8);
          }
        };
        break;
      }
      case 'geyser': {
        // little water spouts round the card's edge, `count` of them evenly spaced: each shoots up, falls back and
        // shoots again, throwing spray off its top
        const count = layer.count ?? 1;
        const height = layer.size ?? 3;
        const spouts: { col: THREE.Sprite; drops: THREE.Sprite[]; x: number; z: number; ph: number }[] = [];
        for (let i = 0; i < count; i++) {
          const a = (i / count) * Math.PI * 2 + 0.4;
          const c = Math.cos(a);
          const sn = Math.sin(a);
          const x = (CW / 2 + 0.5) * Math.sign(c) * Math.pow(Math.abs(c), 0.5);
          const z = (CH / 2 + 0.5) * Math.sign(sn) * Math.pow(Math.abs(sn), 0.5);
          const col = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.riser, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
          col.center.set(0.5, 0); // (a jet: its head bright, a thinning column below it down to the table)
          const drops: THREE.Sprite[] = [];
          for (let d = 0; d < 5; d++) drops.push(add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: new THREE.Color(colors[Math.min(1, colors.length - 1)]), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite);
          spouts.push({ col, drops, x, z, ph: i / count });
        }
        part.update = (k, time) => {
          const env = Math.min(1, k * 4) * Math.min(1, (1 - k) * 4);
          for (const sp of spouts) {
            const cyc = (time * 0.9 + sp.ph) % 1; // up, then back down
            const up = Math.sin(Math.PI * Math.min(1, cyc / 0.8));
            const h = height * (0.25 + 0.75 * up);
            sp.col.position.set(sp.x, 0.1, sp.z);
            sp.col.scale.set(1.4 + 0.5 * up, h, 1);
            sp.col.material.opacity = Math.min(1, strength * env * (0.5 + 0.5 * up));
            sp.drops.forEach((d, j) => {
              const q = (cyc + j * 0.17) % 1;
              const ang = j * 1.3 + sp.ph * 6;
              d.position.set(sp.x + Math.cos(ang) * q * 0.9, 0.1 + h * (1 - (q - 0.35) * (q - 0.35) * 2.2), sp.z + Math.sin(ang) * q * 0.9);
              d.scale.set(0.6, 0.6, 1);
              d.material.opacity = Math.min(1, strength * env * up * (1 - q));
            });
          }
        };
        break;
      }
      case 'lift': {
        // the card itself rises off the table as it goes (the table reads `lift` off the card while it bursts)
        const height = layer.size ?? 4;
        part.update = (k) => {
          follow.userData.lift = height * ease(k);
        };
        break;
      }
      case 'strike': {
        // lightning straight down out of the sky onto it: a jagged bolt, flickering to a new shape now and then,
        // with a bright spot where it hits
        const count = layer.count ?? 1;
        const height = layer.size ?? 18;
        const bolts: { sp: THREE.Sprite; x: number; z: number; next: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.bolts[i % t.bolts.length], color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, rotation: -Math.PI / 2 }))) as THREE.Sprite;
          sp.center.set(0, 0.5); // the bolt's foot (the left of the picture) is where it strikes
          bolts.push({ sp, x: (Math.random() - 0.5) * CW * 0.5, z: (Math.random() - 0.5) * CH * 0.5, next: 0 });
        }
        const spot = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: new THREE.Color(colors[Math.min(1, colors.length - 1)]), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
        spot.position.set(0, 0.5, 0);
        part.update = (k) => {
          const on = k < 0.15 ? k / 0.15 : Math.pow(1 - (k - 0.15) / 0.85, 1.3);
          for (const b of bolts) {
            if (k >= b.next) {
              b.next = k + 0.12 + Math.random() * 0.1;
              b.sp.material.map = t.bolts[Math.floor(Math.random() * t.bolts.length)];
              b.sp.material.rotation = -Math.PI / 2 + (Math.random() - 0.5) * 0.15;
            }
            b.sp.position.set(b.x, 0.3, b.z);
            b.sp.scale.set(height, height * 0.3, 1);
            b.sp.material.opacity = Math.min(1, strength * on * (0.7 + 0.3 * Math.random()));
          }
          const sz = 5 * (0.6 + on);
          spot.scale.set(sz, sz, 1);
          spot.material.opacity = Math.min(1, strength * on);
        };
        break;
      }
      case 'vortex': {
        // a black hole hanging over the spot: a dark core, and clouds of its colors wheeling round it and drawn in
        const count = layer.count ?? 12;
        const reach = layer.size ?? 6;
        const big = layer.dot ?? 3;
        const yHigh = 3.5;
        const core = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: new THREE.Color(colors[0]), transparent: true, opacity: 0, depthWrite: false }))) as THREE.Sprite;
        core.position.set(0, yHigh, 0);
        const rim = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: new THREE.Color(colors[Math.min(1, colors.length - 1)]), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
        rim.position.set(0, yHigh - 0.05, 0);
        const clouds: { sp: THREE.Sprite; a: number; r: number; turn: number; y: number }[] = [];
        for (let i = 0; i < count; i++) {
          const c = new THREE.Color(colors[1 + (i % Math.max(1, colors.length - 1))] ?? colors[0]);
          const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.cloud, color: c, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
          clouds.push({ sp, a: (i / count) * Math.PI * 2, r: reach * (0.5 + Math.random() * 0.5), turn: 0.8 + Math.random() * 0.6, y: yHigh + (Math.random() - 0.5) * 1.2 });
        }
        part.update = (k, time) => {
          const env = Math.min(1, k * 4) * Math.min(1, (1 - k) * 4);
          const pulse = 1 + 0.08 * Math.sin(time * 3);
          core.scale.set(big * pulse, big * pulse, 1);
          core.material.opacity = Math.min(1, strength * env);
          rim.scale.set(big * 1.9 * pulse, big * 1.9 * pulse, 1);
          rim.material.opacity = Math.min(1, strength * env * 0.55);
          for (const c of clouds) {
            const a = c.a + time * c.turn;
            const r = c.r * (0.65 + 0.35 * ((Math.sin(time * 0.7 + c.a) + 1) / 2));
            c.sp.position.set(Math.cos(a) * r, c.y, Math.sin(a) * r * 0.85);
            const sz = big * (0.9 + 0.4 * (r / reach));
            c.sp.scale.set(sz, sz, 1);
            c.sp.material.rotation = a;
            c.sp.material.opacity = Math.min(1, strength * env * 0.6);
          }
        };
        break;
      }
      case 'fireflies': {
        // motes drifting round the card on slow looping paths, each glowing brighter and dimmer as it goes;
        // they fade in and out at the ends, so rounds played over one another never blink
        const count = layer.count ?? 8;
        const reach = layer.size ?? 6;
        const big = layer.dot ?? 0.8;
        const flies: { sp: THREE.Sprite; cx: number; cz: number; y: number; ax: number; az: number; fx: number; fz: number; ph: number; blink: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
          const a = Math.random() * Math.PI * 2;
          const r = reach * (0.25 + Math.random() * 0.6);
          flies.push({ sp, cx: Math.cos(a) * r, cz: Math.sin(a) * r, y: 0.8 + Math.random() * (1.5 + reach * 0.25), ax: 0.6 + Math.random() * reach * 0.2, az: 0.6 + Math.random() * reach * 0.2, fx: 0.5 + Math.random() * 0.7, fz: 0.4 + Math.random() * 0.7, ph: Math.random() * 6.28, blink: 2 + Math.random() * 3 });
        }
        part.update = (k, time) => {
          const env = Math.min(1, k * 4) * Math.min(1, (1 - k) * 4);
          for (const f of flies) {
            f.sp.position.set(f.cx + Math.sin(time * f.fx + f.ph) * f.ax, f.y + Math.sin(time * 0.9 + f.ph * 2) * 0.6, f.cz + Math.cos(time * f.fz + f.ph) * f.az);
            const glow = 0.45 + 0.55 * Math.max(0, Math.sin(time * f.blink + f.ph));
            const sz = big * (0.7 + 0.5 * glow);
            f.sp.scale.set(sz, sz, 1);
            f.sp.material.opacity = Math.min(1, strength * env * glow);
          }
        };
        break;
      }
      case 'slash': {
        // one blade cut across the card, corner to corner: it is drawn in fast, then fades (two, one flipped: an X).
        // `size` = how far past the corners it reaches, `dot` = how thick it is
        const ang = Math.atan2(CH, CW) * (layer.flip ? -1 : 1);
        const reach = Math.hypot(CW, CH) * (layer.size ?? 1.15);
        const thick = layer.dot ?? 0.7;
        const m = add(new THREE.Mesh(flatPlane(1, 1), material(t.dot))) as THREE.Mesh;
        m.rotation.y = ang;
        m.position.y = 1.2;
        const dir = new THREE.Vector3(Math.cos(ang), 0, -Math.sin(ang));
        part.update = (k) => {
          const draw = ease(Math.min(1, k / 0.3));
          const len = Math.max(0.01, reach * draw);
          m.scale.set(len, 1, thick * (1 - 0.4 * Math.max(0, k - 0.3)));
          m.position.x = dir.x * (-reach / 2 + len / 2);
          m.position.z = dir.z * (-reach / 2 + len / 2);
          (m.material as THREE.MeshBasicMaterial).opacity = Math.min(1, strength * (k < 0.3 ? 1 : Math.pow(1 - (k - 0.3) / 0.7, 1.5)));
        };
        break;
      }
      case 'flock': {
        // birds circling the card high up, wings beating, swooping a little up and down as they go round
        // (dark: black birds painted over the room; otherwise light ones)
        const count = layer.count ?? 6;
        const reach = layer.size ?? 6;
        const big = layer.dot ?? 1.6;
        const speed = layer.speed ?? 0.35;
        const birds: { sp: THREE.Sprite; a: number; r: number; y: number; flap: number; dip: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(
            new THREE.Sprite(new THREE.SpriteMaterial({ map: t.bird, color: layer.dark ? new THREE.Color(color).multiplyScalar(0.6) : color, transparent: true, opacity: 0, depthWrite: false, blending: layer.dark ? THREE.NormalBlending : THREE.AdditiveBlending })),
          ) as THREE.Sprite;
          birds.push({ sp, a: (i / count) * Math.PI * 2 + Math.random() * 0.4, r: reach * (0.75 + Math.random() * 0.45), y: 4 + Math.random() * 3.5, flap: 7 + Math.random() * 4, dip: Math.random() * 6.28 });
        }
        part.update = (k, time) => {
          const env = Math.min(1, k * 6) * Math.min(1, (1 - k) * 5);
          for (const b of birds) {
            const a = b.a + k * layer.duration * speed * Math.PI * 2;
            b.sp.position.set(Math.cos(a) * b.r, b.y + Math.sin(a * 2 + b.dip) * 0.8, Math.sin(a) * b.r);
            const beat = Math.abs(Math.sin(time * b.flap + b.dip)); // wings up, wings down
            b.sp.scale.set(big * (0.75 + 0.25 * beat), big * 0.5 * (0.35 + 0.65 * beat), 1);
            b.sp.material.rotation = Math.sin(a) * 0.35; // banking as it turns
            b.sp.material.opacity = Math.min(1, strength * env);
          }
        };
        break;
      }
      case 'spiral': {
        // ribbons of light winding up round the card in a slow helix, narrowing as they climb
        const count = layer.count ?? 4;
        const height = layer.size ?? 8;
        const turns = layer.speed ?? 1.4;
        const seg = 10;
        const coils: { dots: THREE.Sprite[]; phase: number; start: number; r: number }[] = [];
        for (let i = 0; i < count; i++) {
          const dots: THREE.Sprite[] = [];
          for (let j = 0; j < seg; j++) dots.push(add(new THREE.Sprite(new THREE.SpriteMaterial({ map: j ? t.cloud : t.dot, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite);
          coils.push({ dots, phase: (i / count) * Math.PI * 2, start: (i / count) * 0.35 + Math.random() * 0.05, r: Math.max(CW, CH) * (0.55 + Math.random() * 0.15) });
        }
        const big = layer.dot ?? 1;
        part.update = (k) => {
          for (const c of coils) {
            const local = (k - c.start) / (1 - c.start);
            c.dots.forEach((sp, j) => {
              const s = local - j * 0.03;
              if (s <= 0 || s >= 1) { sp.material.opacity = 0; return; }
              const a = c.phase + s * turns * Math.PI * 2;
              const r = c.r * (1 - 0.55 * s);
              sp.position.set(Math.cos(a) * r, 0.4 + height * s, Math.sin(a) * r);
              const size = big * (j ? 1.3 - j * 0.07 : 0.8);
              sp.scale.set(size, size, 1);
              sp.material.opacity = Math.min(1, strength * Math.sin(Math.PI * s) * (1 - j / seg) * (j ? 0.8 : 1));
            });
          }
        };
        break;
      }
      case 'souls': {
        // pale souls drift in from all around, each a bright head with a misty tail, winding inward and
        // diving into the card
        const count = layer.count ?? 7;
        const reach = layer.size ?? 14;
        const big = layer.dot ?? 1.4;
        const seg = 9;
        const souls: { dots: THREE.Sprite[]; a: number; r: number; start: number; swirl: number; lift: number }[] = [];
        for (let i = 0; i < count; i++) {
          const dots: THREE.Sprite[] = [];
          for (let j = 0; j < seg; j++) dots.push(add(new THREE.Sprite(new THREE.SpriteMaterial({ map: j ? t.cloud : t.dot, color: j ? color : new THREE.Color(colors[Math.min(2, colors.length - 1)]), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite);
          souls.push({ dots, a: (i / count) * Math.PI * 2 + Math.random() * 0.4, r: reach * (0.8 + Math.random() * 0.4), start: Math.random() * 0.35, swirl: (0.9 + Math.random() * 0.8) * (i % 2 ? 1 : -1), lift: 3 + Math.random() * 4 });
        }
        part.update = (k) => {
          for (const so of souls) {
            const local = (k - so.start) / (1 - so.start);
            so.dots.forEach((sp, j) => {
              const s = local - j * 0.035;
              if (s <= 0 || s >= 1) { sp.material.opacity = 0; return; }
              const pull = s * s * (3 - 2 * s);
              const a = so.a + so.swirl * pull * 2;
              const r = so.r * (1 - pull);
              sp.position.set(Math.cos(a) * r, 0.4 + so.lift * Math.sin(Math.PI * Math.min(1, pull * 1.1)) * (1 - pull * 0.6), Math.sin(a) * r);
              const size = big * (j ? 1.6 - j * 0.12 : 1);
              sp.scale.set(size, size, 1);
              sp.material.opacity = Math.min(1, strength * Math.min(1, s * 5) * Math.min(1, (1 - s) * 8) * (j ? 0.5 * (1 - j / seg) : 1));
            });
          }
        };
        break;
      }
      case 'flames': {
        // little tongues of flame flicker up all round the card's edge, dancing higher and lower
        const count = layer.count ?? 10;
        const height = layer.size ?? 2.5;
        const flames: { sp: THREE.Sprite; phase: number; f: number; w: number; a: number }[] = [];
        for (let i = 0; i < count; i++) {
          const sp = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: t.flame, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }))) as THREE.Sprite;
          sp.center.set(0.5, 0.0); // its foot on the table, the tip above
          flames.push({ sp, phase: Math.random() * 6.28, f: 9 + Math.random() * 8, w: (0.45 + Math.random() * 0.3) * height, a: (i / count) * Math.PI * 2 });
        }
        const speed = layer.speed ?? 0; // > 0: the ring of flames turns round the card (fire dancing round it)
        part.update = (k, time) => {
          const env = Math.min(1, k * 5) * Math.min(1, (1 - k) * 4);
          for (const fl of flames) {
            const a = fl.a + k * layer.duration * speed * Math.PI * 2;
            const c = Math.cos(a);
            const sn = Math.sin(a);
            // round the card's outline (a squared-off ellipse), just outside it
            const x = (CW / 2 + 0.6) * Math.sign(c) * Math.pow(Math.abs(c), 0.5);
            const z = (CH / 2 + 0.6) * Math.sign(sn) * Math.pow(Math.abs(sn), 0.5);
            fl.sp.position.set(x, 0.15, z);
            const lick = 0.55 + 0.25 * Math.sin(time * fl.f + fl.phase) + 0.2 * Math.sin(time * fl.f * 1.7 + fl.phase * 2);
            fl.sp.scale.set(fl.w, height * lick, 1);
            fl.sp.material.opacity = Math.min(1, strength * env * (0.7 + 0.3 * lick));
          }
        };
        break;
      }
    }
    return part;
  }

  /** Takes effects of these kinds off a card at once (a looping aura that no longer applies). */
  stop(follow: THREE.Object3D, kinds: string[]) {
    const gone = this.running.filter((fx) => fx.follow === follow && kinds.includes(fx.kind));
    for (const fx of gone) this.remove(fx);
    if (gone.length) this.running = this.running.filter((fx) => !gone.includes(fx));
  }

  private remove(fx: RunningEffect) {
    this.scene.remove(fx.group);
    if (fx.follow && fx.parts.some((p) => p.layer.kind === 'rumble')) fx.follow.userData.rumble = 0; // stopped part-way: the card settles
    fx.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        const m = o.material as THREE.MeshBasicMaterial;
        if (m.userData.ownMap) m.map?.dispose(); // a texture copy made for this effect alone (a sheen)
        m.dispose();
      } else if (o instanceof THREE.Sprite) {
        o.material.dispose();
      }
    });
  }

  /**
   * How big the effects on a thing are drawn: a card shrunk to share a lane (or attached to another's edge) has
   * its Entrance, its passive scenes and every other effect on it at its own smaller size. (A card's size is the
   * scale of its root across the table; anything else is full size.)
   */
  private sizeOf(follow: THREE.Object3D) {
    const sc = follow.scale.x;
    return Number.isFinite(sc) && sc > 0.05 && sc < 0.999 ? sc : 1;
  }

  /** Called every frame. */
  update() {
    const now = performance.now() / 1000;
    for (const fx of this.running) {
      if (fx.follow?.parent) {
        fx.group.position.copy(fx.follow.position);
        // a card in mid-air on its way down (an Entrance) keeps its effects on the table under it
        if (typeof fx.follow.userData.fxY === 'number') fx.group.position.y = fx.follow.userData.fxY;
        fx.group.rotation.y = fx.follow.rotation.y;
        const sc = this.sizeOf(fx.follow);
        if (Math.abs(fx.group.scale.x - sc) > 1e-3) fx.group.scale.setScalar(sc);
      }
      for (const p of fx.parts) {
        const k = (now - p.start) / p.layer.duration;
        if (k < 0) continue;
        if (!p.started) {
          p.started = true;
          for (const o of p.objects) o.visible = true;
        }
        p.update(Math.min(1, k), now);
        if (k >= 1) for (const o of p.objects) o.visible = false;
      }
    }
    const done = this.running.filter((fx) => now - fx.born > fx.length + 0.05);
    for (const fx of done) this.remove(fx);
    if (done.length) this.running = this.running.filter((fx) => !done.includes(fx));
  }

  dispose() {
    for (const fx of this.running) this.scene.remove(fx.group);
    this.running = [];
  }
}

// =====================================================================
//  Discard aura: lights that circle a card like electrons around an atom.
//  The bigger the owner's discard pile, the more lights, the brighter and
//  the faster. Settings are in src/config.ts under discardAura.
// =====================================================================
const TRAIL = 9;
const RINGS = 3;
const RING_POINTS = 72;

interface AuraLight {
  core: THREE.Sprite; // solid dot, so even the black part of the gradient shows up
  halo: THREE.Sprite; // soft glow around it
  trail: THREE.Sprite[];
  ring: number;
  phase: number;
}

export class OrbitAura {
  readonly group = new THREE.Group();
  private lights: AuraLight[] = [];
  private rings: THREE.LineLoop[] = []; // the faint paths the lights travel along
  private stops: THREE.Color[];
  private tmp = new THREE.Color();

  constructor() {
    const cfg = CONFIG.discardAura;
    this.stops = cfg.colors.map((c) => new THREE.Color(c));
    const dot = tex().dot;
    const sprite = (blending: THREE.Blending) => {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: dot, transparent: true, depthWrite: false, blending }));
      this.group.add(sp);
      return sp;
    };
    for (let r = 0; r < RINGS; r++) {
      const points: THREE.Vector3[] = [];
      for (let j = 0; j < RING_POINTS; j++) points.push(this.pointOn(r, (j / RING_POINTS) * Math.PI * 2, new THREE.Vector3()));
      const ring = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
      );
      this.rings.push(ring);
      this.group.add(ring);
    }
    const perRing = Math.ceil(cfg.maxLights / RINGS);
    for (let i = 0; i < cfg.maxLights; i++) {
      const ring = i % RINGS;
      this.lights.push({
        halo: sprite(THREE.AdditiveBlending),
        trail: Array.from({ length: TRAIL }, () => sprite(THREE.NormalBlending)),
        core: sprite(THREE.NormalBlending),
        ring,
        phase: (Math.floor(i / RINGS) / perRing) * Math.PI * 2 + ring * 0.9,
      });
    }
  }

  /** Blends through the color list and back to the start. t goes 0..1. */
  private colorAt(t: number, out: THREE.Color) {
    const n = this.stops.length;
    const f = (((t % 1) + 1) % 1) * n;
    const i = Math.floor(f);
    return out.copy(this.stops[i % n]).lerp(this.stops[(i + 1) % n], f - i);
  }

  /** Where a light is on its ring: three flat ovals turned 60° apart, each tilted up on one side. */
  private pointOn(ring: number, angle: number, out: THREE.Vector3) {
    const x = Math.cos(angle) * (CH * 0.72);
    const z = Math.sin(angle) * (CW * 0.4);
    const turn = (ring * Math.PI) / RINGS;
    return out.set(x * Math.cos(turn) - z * Math.sin(turn), 1.5 + Math.sin(angle) * 1.05, x * Math.sin(turn) + z * Math.cos(turn));
  }
  private place(sp: THREE.Sprite, ring: number, angle: number) {
    this.pointOn(ring, angle, sp.position);
  }

  /** Call every frame. pileSize = cards in the owner's discard pile. */
  update(time: number, pileSize: number, shown: boolean) {
    this.group.visible = shown;
    if (!shown) return;
    const cfg = CONFIG.discardAura;
    const k = Math.min(1, pileSize / cfg.fullAt);
    const lit = pileSize <= 0 ? 1 : Math.max(1, Math.round(k * cfg.maxLights));
    const strength = pileSize <= 0 ? 0.3 : 0.55 + 0.45 * k;
    const speed = 0.55 + 0.55 * k; // slow turns, a little quicker as the pile grows
    const size = 0.6 + 0.55 * k;
    this.rings.forEach((ring, r) => {
      const mat = ring.material as THREE.LineBasicMaterial;
      ring.visible = r < lit; // a path shows once a light is travelling on it
      this.colorAt(time / cfg.cycleSeconds + r / RINGS, mat.color);
      mat.opacity = 0.12 + 0.2 * k;
    });
    this.lights.forEach((light, i) => {
      const on = i < lit;
      light.core.visible = light.halo.visible = on;
      for (const tr of light.trail) tr.visible = on;
      if (!on) return;
      const angle = time * speed * (light.ring % 2 ? -1 : 1) + light.phase;
      const color = this.colorAt(time / cfg.cycleSeconds + i / cfg.maxLights, this.tmp);
      this.place(light.core, light.ring, angle);
      light.core.material.color.copy(color);
      light.core.material.opacity = strength;
      light.core.scale.setScalar(size);
      light.halo.position.copy(light.core.position);
      light.halo.material.color.copy(color);
      light.halo.material.opacity = strength * 0.55;
      light.halo.scale.setScalar(size * 2);
      light.trail.forEach((tr, j) => {
        const fade = 1 - (j + 1) / (TRAIL + 1);
        this.place(tr, light.ring, angle - (light.ring % 2 ? -1 : 1) * 0.085 * (j + 1));
        tr.material.color.copy(color);
        tr.material.opacity = strength * fade * 0.75;
        tr.scale.setScalar(size * (0.3 + 0.6 * fade));
      });
    });
  }

  dispose() {
    this.group.removeFromParent();
    for (const light of this.lights) for (const sp of [light.core, light.halo, ...light.trail]) sp.material.dispose();
    for (const ring of this.rings) {
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
    }
  }
}
